import React, { useState, useEffect, useCallback } from "react";
import ReactApexChart from "react-apexcharts";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  Add as AddIcon,
  BarChart as BarChartIcon,
  ShowChart as LineChartIcon,
  PieChart as PieChartIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Close as CloseIcon,
  Cancel as CancelIcon,
  PhoneMissed as PhoneMissedIcon,
  FreeBreakfast as FreeIcon,
  Paid as PaidIcon,
  AttachMoney as RevenueIcon,
  AccessTime as TimeIcon,
  TrendingUp as TrendingUpIcon,
  Search as SearchIcon,
  CalendarToday as CalendarIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  fetchConsultantPerformanceApi,
  fetchConsultantPerformanceGraphApi,
  fetchMainMetricsApi,
  fetchConsultantListApi,
} from "../../services/consultantPerformance";
import { toastMessage } from "../../utils/toastMessage";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import useDebounce from "../../hooks/useDebounce";
import scrollToUp from "../../utils/scrollToUp";

// Simple React Graph Component (Fallback)
const SimpleGraph = ({ type, data, labels, series, height = 450, startDate, endDate }) => {
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  
  // Smart date formatting based on date range
  const getDateRangeFormat = (start, end) => {
    if (!start || !end) return 'day';
    const diffTime = Math.abs(new Date(end) - new Date(start));
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 1) {
      return 'hour'; // Show hours for same day or 1 day
    } else if (diffDays <= 7) {
      return 'day'; // Show days for up to a week
    } else if (diffDays <= 31) {
      return 'day'; // Show days for up to a month
    } else if (diffDays <= 365) {
      return 'month'; // Show months for up to a year
    } else {
      return 'year'; // Show years for more than a year
    }
  };

  // Format date based on range type
  const formatDateByRange = (date, rangeType) => {
    if (!date) return '';
    const d = new Date(date);
    
    switch (rangeType) {
      case 'hour':
        return d.toLocaleString('en-IN', { 
          day: '2-digit', 
          month: 'short', 
          hour: '2-digit', 
          minute: '2-digit' 
        });
      case 'day':
        return d.toLocaleDateString('en-IN', { 
          day: '2-digit', 
          month: 'short' 
        });
      case 'month':
        return d.toLocaleDateString('en-IN', { 
          month: 'short', 
          year: 'numeric' 
        });
      case 'year':
        return d.toLocaleDateString('en-IN', { 
          year: 'numeric' 
        });
      default:
        return d.toLocaleDateString('en-IN', { 
          day: '2-digit', 
          month: 'short', 
          year: 'numeric' 
        });
    }
  };

  const dateRangeType = startDate && endDate ? getDateRangeFormat(startDate, endDate) : 'day';
  // Enhanced colors for better visibility - using distinct, vibrant colors
  const colors = ["#1976d2", "#d32f2f", "#388e3c", "#f57c00", "#7b1fa2", "#0288d1", "#c2185b"];
  
  const formatValue = (val) => {
    if (val === undefined || val === null || isNaN(val)) {
      return "0";
    }
    if (val >= 1000000) return (val / 1000000).toFixed(2) + "M";
    if (val >= 1000) return (val / 1000).toFixed(2) + "K";
    return val.toFixed(2);
  };

  const getMaxValue = () => {
    if (!series || series.length === 0) return 100;
    const allValues = series.flatMap(s => s.data || [s]).filter(v => typeof v === 'number');
    return Math.max(...allValues, 100);
  };

  const maxValue = getMaxValue();
  const chartHeight = height - 100;
  const chartWidth = "100%";

  // Pie/Donut Chart
  if (type === "pie" || type === "donut") {
    const pieData = Array.isArray(data) ? data : (series && series[0] && series[0].data ? series[0].data : []);
    const pieLabels = labels || pieData.map((_, i) => `Item ${i + 1}`);
    const total = pieData.reduce((sum, val) => sum + (typeof val === 'number' ? val : parseFloat(val) || 0), 0);
    
    let currentAngle = -90;
    const radius = type === "donut" ? 80 : 100;
    const centerX = 200;
    const centerY = 200;

    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 3 }}>
        <svg width="400" height="400" viewBox="0 0 400 400">
          {pieData.map((value, index) => {
            const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
            const percentage = total > 0 ? (numValue / total) * 100 : 0;
            const angle = (percentage / 100) * 360;
            const largeArc = angle > 180 ? 1 : 0;
            
            const x1 = centerX + radius * Math.cos((currentAngle * Math.PI) / 180);
            const y1 = centerY + radius * Math.sin((currentAngle * Math.PI) / 180);
            const x2 = centerX + radius * Math.cos(((currentAngle + angle) * Math.PI) / 180);
            const y2 = centerY + radius * Math.sin(((currentAngle + angle) * Math.PI) / 180);
            
            const path = type === "donut" 
              ? `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`
              : `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
            
            const color = colors[index % colors.length];
            currentAngle += angle;
            
            return (
              <g key={index}>
                <path
                  d={path}
                  fill={color}
                  stroke="#fff"
                  strokeWidth="2"
                  style={{ cursor: "pointer" }}
                />
                {percentage > 5 && (
                  <text
                    x={centerX + (radius * 0.7) * Math.cos(((currentAngle - angle / 2) * Math.PI) / 180)}
                    y={centerY + (radius * 0.7) * Math.sin(((currentAngle - angle / 2) * Math.PI) / 180)}
                    textAnchor="middle"
                    fill="#fff"
                    fontSize="12"
                    fontWeight="bold"
                  >
                    {percentage.toFixed(1)}%
                  </text>
                )}
              </g>
            );
          })}
          {type === "donut" && (
            <circle cx={centerX} cy={centerY} r="60" fill="#fff" />
          )}
        </svg>
        <Box sx={{ ml: 3 }}>
          {pieLabels.slice(0, pieData.length).map((label, index) => (
            <Box key={index} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  bgcolor: colors[index % colors.length],
                  borderRadius: "50%",
                  mr: 1,
                }}
              />
              <Typography variant="body2" sx={{ color: "#424242" }}>
                {label}: {formatValue(pieData[index] || 0)} ({total > 0 ? ((pieData[index] || 0) / total * 100).toFixed(1) : 0}%)
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // Bar Chart
  if (type === "bar") {
    const containerWidth = 800; // Fixed container width
    const barWidth = containerWidth / (labels?.length || 1) / (series?.length || 1) - 10;
    const barSpacing = 5;
    const padding = 80; // Increased padding for better visibility
    const legendHeight = series && series.length > 0 ? 40 : 0;

    return (
      <Box sx={{ p: 3, width: "100%", overflow: "hidden" }}>
        {/* Legend */}
        {series && series.length > 0 && (
          <Box sx={{ 
            display: "flex", 
            justifyContent: "center", 
            alignItems: "center", 
            gap: 3, 
            mb: 2,
            flexWrap: "wrap",
            pb: 2,
            borderBottom: "2px solid #e0e0e0"
          }}>
            {series.map((s, seriesIndex) => {
              const color = colors[seriesIndex % colors.length];
              const seriesName = s.name || `Series ${seriesIndex + 1}`;
              return (
                <Box 
                  key={seriesIndex} 
                  sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    gap: 1,
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: "#f5f5f5",
                    "&:hover": { bgcolor: "#eeeeee" }
                  }}
                >
                  <Box
                    sx={{
                      width: 20,
                      height: 16,
                      bgcolor: color,
                      borderRadius: 1,
                    }}
                  />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 600, 
                      fontSize: "0.95rem",
                      color: "#212121"
                    }}
                  >
                    {seriesName}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
        
        <svg width="100%" height={height - legendHeight} viewBox={`0 0 ${containerWidth} ${height - legendHeight}`} preserveAspectRatio="xMidYMid meet">
          {/* Grid lines - more visible */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
            const y = padding + (1 - ratio) * (chartHeight - padding);
            return (
              <line
                key={ratio}
                x1={padding}
                y1={y}
                x2={containerWidth - 20}
                y2={y}
                stroke="#d0d0d0"
                strokeWidth="1.5"
                strokeDasharray="3,3"
                opacity="0.8"
              />
            );
          })}
          
          {/* Y-axis - thicker */}
          <line x1={padding} y1={padding} x2={padding} y2={chartHeight} stroke="#424242" strokeWidth="3" />
          {/* X-axis - thicker */}
          <line x1={padding} y1={chartHeight} x2={containerWidth - 20} y2={chartHeight} stroke="#424242" strokeWidth="3" />
          
          {/* Y-axis labels - larger font */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
            const y = padding + (1 - ratio) * (chartHeight - padding);
            const value = maxValue * ratio;
            return (
              <g key={ratio}>
                <line x1={padding - 5} y1={y} x2={padding} y2={y} stroke="#424242" strokeWidth="2" />
                <text x={padding - 15} y={y + 6} textAnchor="end" fontSize="14" fill="#212121" fontWeight="600">
                  {formatValue(value)}
                </text>
              </g>
            );
          })}

          {/* Bars */}
          {series?.map((s, seriesIndex) => {
            const dataArray = s.data || [s];
            return dataArray.map((value, index) => {
              const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
              const barHeight = (numValue / maxValue) * (chartHeight - padding);
              const x = padding + 10 + index * (barWidth + barSpacing) * (series?.length || 1) + seriesIndex * (barWidth + barSpacing);
              const y = chartHeight - barHeight;
              
              return (
                <g key={`${seriesIndex}-${index}`}>
                  <rect
                    x={x}
                    y={y}
                    width={barWidth}
                    height={barHeight}
                    fill={colors[seriesIndex % colors.length]}
                    rx="4"
                    style={{ cursor: "pointer" }}
                    stroke="#fff"
                    strokeWidth="1"
                  />
                  {barHeight > 25 && (
                    <text
                      x={x + barWidth / 2}
                      y={y - 8}
                      textAnchor="middle"
                      fontSize="12"
                      fill="#212121"
                      fontWeight="600"
                    >
                      {formatValue(numValue)}
                    </text>
                  )}
                </g>
              );
            });
          })}

          {/* X-axis labels - larger font */}
          {labels?.map((label, index) => {
            const x = padding + 10 + index * (barWidth + barSpacing) * (series?.length || 1) + ((series?.length || 1) * (barWidth + barSpacing)) / 2;
            return (
              <g key={index}>
                <line x1={x} y1={chartHeight} x2={x} y2={chartHeight + 5} stroke="#424242" strokeWidth="2" />
                <text
                  x={x}
                  y={chartHeight + 25}
                  textAnchor="middle"
                  fontSize="13"
                  fill="#212121"
                  fontWeight="600"
                  transform={`rotate(-45 ${x} ${chartHeight + 25})`}
                >
                  {String(label).substring(0, 12)}
                </text>
              </g>
            );
          })}
        </svg>
      </Box>
    );
  }

  // Line/Area Chart
  if (type === "line" || type === "area") {
    const pointRadius = 7; // Increased for better visibility
    const padding = 120; // Increased padding for better spacing (left side for dates)
    const labelSpacing = 120; // Space between each label on X-axis
    const totalLabels = labels?.length || 1;
    // Dynamic width based on number of labels - minimum 1000, add 120px per label
    const containerWidth = Math.max(1000, padding + (totalLabels * labelSpacing) + 100);
    const legendHeight = series && series.length > 0 ? 50 : 0; // Space for legend
    const actualChartHeight = height - legendHeight; // Actual chart height after accounting for legend
    const svgChartHeight = actualChartHeight - 180; // Chart area height within SVG (more space for labels and dates)
    const xAxisLabelHeight = 140; // Space for X-axis labels and dates (horizontal)

    return (
      <Box sx={{ 
        width: "100%",
        bgcolor: "white",
        borderRadius: 3,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        overflow: "hidden"
      }}>
        {/* Graph Header with Legend */}
        <Box sx={{ 
          p: 3,
          borderBottom: "2px solid #f0f0f0",
          bgcolor: "#fafafa"
        }}>
          {series && series.length > 0 && (
            <Box sx={{ 
              display: "flex", 
              justifyContent: "flex-start", 
              alignItems: "center", 
              gap: 3, 
              flexWrap: "wrap"
            }}>
              {series.map((s, seriesIndex) => {
                const color = colors[seriesIndex % colors.length];
                const seriesName = s.name || `Series ${seriesIndex + 1}`;
                return (
                  <Box 
                    key={seriesIndex} 
                    sx={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: 1.5,
                      px: 2.5,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: "white",
                      border: `1.5px solid ${color}40`,
                      boxShadow: "0 1px 4px rgba(0,0,0,0.08)"
                    }}
                  >
                    <Box
                      sx={{
                        width: 20,
                        height: 4,
                        bgcolor: color,
                        borderRadius: 1
                      }}
                    />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 600, 
                        fontSize: "0.875rem",
                        color: "#424242"
                      }}
                    >
                      {seriesName}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
        
        {/* Scrollable Graph Container */}
        <Box sx={{ 
          width: "100%", 
          position: "relative",
          bgcolor: "white"
        }}>
          <Box sx={{ 
            width: "100%", 
            overflowX: "auto", 
            overflowY: "visible",
            p: 3,
            "&::-webkit-scrollbar": {
              height: "10px"
            },
            "&::-webkit-scrollbar-track": {
              background: "#f5f5f5",
              borderRadius: "5px"
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#c0c0c0",
              borderRadius: "5px",
              "&:hover": {
                background: "#a0a0a0"
              }
            }
          }}>
            <Box sx={{ minWidth: containerWidth, position: "relative" }}>
            <svg 
              width={containerWidth} 
              height={actualChartHeight + xAxisLabelHeight} 
              viewBox={`0 0 ${containerWidth} ${actualChartHeight + xAxisLabelHeight}`}
              onMouseMove={(e) => {
                if (!series || series.length === 0) return;
                const svg = e.currentTarget;
                const rect = svg.getBoundingClientRect();
                const x = ((e.clientX - rect.left) / rect.width) * containerWidth;
                const y = ((e.clientY - rect.top) / rect.height) * (actualChartHeight + xAxisLabelHeight);
                
                // Find closest point across all series
                let closestPoint = null;
                let closestDist = Infinity;
                let closestSeriesIndex = 0;
                let closestPointIndex = 0;
                
                series.forEach((s, seriesIndex) => {
                  const dataArray = s.data || [s];
                  const color = colors[seriesIndex % colors.length];
                  const points = dataArray.map((value, index) => {
                    const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
                    const px = padding + (index * labelSpacing);
                    const py = svgChartHeight - ((numValue / maxValue) * (svgChartHeight - padding));
                    return { x: px, y: py, value: numValue, index };
                  });
                  
                  points.forEach((p) => {
                    const dist = Math.sqrt(Math.pow(x - p.x, 2) + Math.pow(y - p.y, 2));
                    if (dist < closestDist && dist < 50) { // Only if within 50px
                      closestDist = dist;
                      closestPoint = p;
                      closestSeriesIndex = seriesIndex;
                      closestPointIndex = p.index;
                    }
                  });
                });
                
                if (closestPoint) {
                  const s = series[closestSeriesIndex];
                  const label = labels?.[closestPointIndex] || '';
                  const date = s?.dates?.[closestPointIndex] || null;
                  const seriesName = s?.name || `Series ${closestSeriesIndex + 1}`;
                  const color = colors[closestSeriesIndex % colors.length];
                  
                  setHoveredPoint({
                    x: closestPoint.x,
                    y: closestPoint.y,
                    value: closestPoint.value,
                    label: label,
                    date: date,
                    color: color,
                    seriesName: seriesName
                  });
                  setTooltipPosition({
                    x: closestPoint.x,
                    y: Math.max(closestPoint.y - 30, padding + 20)
                  });
                }
              }}
              onMouseLeave={() => {
                setHoveredPoint(null);
              }}
            >
          {/* Grid lines - more visible */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
            const y = padding + (1 - ratio) * (svgChartHeight - padding);
            return (
              <line
                key={ratio}
                x1={padding}
                y1={y}
                x2={containerWidth - 20}
                y2={y}
                stroke="#d0d0d0"
                strokeWidth="1.5"
                strokeDasharray="3,3"
                opacity="0.8"
              />
            );
          })}

          {/* Y-axis - thicker */}
          <line x1={padding} y1={padding} x2={padding} y2={svgChartHeight} stroke="#424242" strokeWidth="3" />
          {/* X-axis - thicker */}
          <line x1={padding} y1={svgChartHeight} x2={containerWidth - 20} y2={svgChartHeight} stroke="#424242" strokeWidth="3" />

          {/* Y-axis labels - larger font with date range info */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
            const y = padding + (1 - ratio) * (svgChartHeight - padding);
            const value = maxValue * ratio;
            return (
              <g key={ratio}>
                <line 
                  x1={padding - 5} 
                  y1={y} 
                  x2={padding} 
                  y2={y} 
                  stroke="#424242" 
                  strokeWidth="2" 
                />
                <text 
                  x={padding - 20} 
                  y={y + 6} 
                  textAnchor="end" 
                  fontSize="14" 
                  fill="#212121"
                  fontWeight="600"
                >
                  {formatValue(value)}
                </text>
              </g>
            );
          })}
          
          {/* Date Range Display on Left Side */}
          {series?.[0]?.dates && series[0].dates.length > 0 && (
            <g>
              <rect
                x="10"
                y={padding + 20}
                width="90"
                height="60"
                fill="white"
                stroke="#1976d2"
                strokeWidth="2"
                rx="4"
                opacity="0.95"
                style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}
              />
              <text
                x="55"
                y={padding + 40}
                textAnchor="middle"
                fontSize="11"
                fill="#1976d2"
                fontWeight="700"
              >
                Date Range
              </text>
              <text
                x="55"
                y={padding + 55}
                textAnchor="middle"
                fontSize="10"
                fill="#424242"
                fontWeight="500"
              >
                {new Date(series[0].dates[0]).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
              </text>
              <text
                x="55"
                y={padding + 68}
                textAnchor="middle"
                fontSize="10"
                fill="#424242"
                fontWeight="500"
              >
                {series[0].dates.length > 1 && new Date(series[0].dates[series[0].dates.length - 1]).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
              </text>
            </g>
          )}

          {/* Draw series */}
          {series?.map((s, seriesIndex) => {
            const dataArray = s.data || [s];
            const color = colors[seriesIndex % colors.length];
            const points = dataArray.map((value, index) => {
              const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
              // Use consistent spacing with X-axis labels
              const x = padding + (index * labelSpacing);
              const y = svgChartHeight - ((numValue / maxValue) * (svgChartHeight - padding));
              return { x, y, value: numValue };
            });

            // Draw area (for area chart)
            if (type === "area" && points.length > 0) {
              const areaPath = `M ${points[0].x} ${svgChartHeight} ${points.map(p => `L ${p.x} ${p.y}`).join(' ')} L ${points[points.length - 1].x} ${svgChartHeight} Z`;
              
              // Find only the most important points to label (reduce clutter)
              const getImportantPoints = () => {
                if (points.length === 0) return [];
                const important = [];
                const maxPoint = points.reduce((max, p) => p.value > max.value ? p : max, points[0]);
                
                // Only show max point and maybe one or two significant peaks
                important.push(maxPoint);
                
                // Add only the top 2-3 local peaks (not all)
                const peaks = [];
                for (let i = 1; i < points.length - 1; i++) {
                  if (points[i].value > points[i-1].value && points[i].value > points[i+1].value) {
                    if (points[i].value > maxValue * 0.4) { // Only significant peaks
                      peaks.push(points[i]);
                    }
                  }
                }
                // Sort by value and take top 2
                peaks.sort((a, b) => b.value - a.value);
                important.push(...peaks.slice(0, 2));
                
                return important;
              };
              
              const importantPoints = getImportantPoints();
              
              return (
                <g key={seriesIndex}>
                  <path d={areaPath} fill={color} fillOpacity="0.2" />
                  <path
                    d={`M ${points.map(p => `${p.x} ${p.y}`).join(' L ')}`}
                    fill="none"
                    stroke={color}
                    strokeWidth="5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}
                  />
                  {points.map((p, i) => {
                    const isImportant = importantPoints.some(ip => ip.x === p.x && ip.y === p.y);
                    const label = labels?.[i] || '';
                    const date = series?.[0]?.dates?.[i] || null;
                    const seriesName = s.name || `Series ${seriesIndex + 1}`;
                    
                    return (
                      <g key={i}>
                        {/* Invisible larger circle for easier hover */}
                        <circle 
                          cx={p.x} 
                          cy={p.y} 
                          r={pointRadius + 8}
                          fill="transparent"
                          style={{ cursor: 'pointer' }}
                        />
                        <circle 
                          cx={p.x} 
                          cy={p.y} 
                          r={isImportant ? pointRadius + 3 : pointRadius + 1} 
                          fill="white" 
                          stroke={color} 
                          strokeWidth={isImportant ? "3" : "2"}
                          style={{ cursor: 'pointer' }}
                        />
                        <circle 
                          cx={p.x} 
                          cy={p.y} 
                          r={isImportant ? pointRadius + 1 : pointRadius} 
                          fill={color}
                          style={{ cursor: 'pointer' }}
                        />
                        {/* Value labels on important points - only show if not hovering */}
                        {isImportant && !hoveredPoint && (
                          <g>
                            <rect
                              x={p.x - 35}
                              y={p.y - 30}
                              width="70"
                              height="20"
                              fill="white"
                              stroke={color}
                              strokeWidth="2"
                              rx="4"
                              opacity="0.95"
                              style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}
                            />
                            <text
                              x={p.x}
                              y={p.y - 15}
                              textAnchor="middle"
                              fontSize="11"
                              fill={color}
                              fontWeight="700"
                            >
                              {formatValue(p.value)}
                            </text>
                          </g>
                        )}
                      </g>
                    );
                  })}
                </g>
              );
            }

            // Draw line - thicker and more visible
            // Find only the most important points to label (reduce clutter)
            const getImportantPoints = () => {
              if (points.length === 0) return [];
              const important = [];
              const maxPoint = points.reduce((max, p) => p.value > max.value ? p : max, points[0]);
              
              // Only show max point and maybe one or two significant peaks
              important.push(maxPoint);
              
              // Add only the top 2-3 local peaks (not all)
              const peaks = [];
              for (let i = 1; i < points.length - 1; i++) {
                if (points[i].value > points[i-1].value && points[i].value > points[i+1].value) {
                  if (points[i].value > maxValue * 0.4) { // Only significant peaks
                    peaks.push(points[i]);
                  }
                }
              }
              // Sort by value and take top 2
              peaks.sort((a, b) => b.value - a.value);
              important.push(...peaks.slice(0, 2));
              
              return important;
            };
            
            const importantPoints = getImportantPoints();
            
            return (
              <g key={seriesIndex}>
                {/* Invisible wider path for easier hover */}
                <path
                  d={`M ${points.map(p => `${p.x} ${p.y}`).join(' L ')}`}
                  fill="none"
                  stroke="transparent"
                  strokeWidth="20"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ cursor: 'crosshair' }}
                />
                <path
                  d={`M ${points.map(p => `${p.x} ${p.y}`).join(' L ')}`}
                  fill="none"
                  stroke={color}
                  strokeWidth="5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))', cursor: 'crosshair' }}
                />
                {points.map((p, i) => {
                  const isImportant = importantPoints.some(ip => ip.x === p.x && ip.y === p.y);
                  const label = labels?.[i] || '';
                  const date = series?.[0]?.dates?.[i] || null;
                  const seriesName = s.name || `Series ${seriesIndex + 1}`;
                  
                  return (
                    <g key={i}>
                      {/* Invisible larger circle for easier hover */}
                      <circle 
                        cx={p.x} 
                        cy={p.y} 
                        r={pointRadius + 8}
                        fill="transparent"
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setHoveredPoint({
                            x: p.x,
                            y: p.y,
                            value: p.value,
                            label: label,
                            date: date,
                            color: color,
                            seriesName: seriesName
                          });
                          setTooltipPosition({
                            x: p.x,
                            y: p.y - 20
                          });
                        }}
                        onMouseLeave={() => {
                          setHoveredPoint(null);
                        }}
                      />
                      <circle 
                        cx={p.x} 
                        cy={p.y} 
                        r={isImportant ? pointRadius + 3 : pointRadius + 1} 
                        fill="white" 
                        stroke={color} 
                        strokeWidth={isImportant ? "3" : "2"}
                        style={{ cursor: 'pointer' }}
                      />
                      <circle 
                        cx={p.x} 
                        cy={p.y} 
                        r={isImportant ? pointRadius + 1 : pointRadius} 
                        fill={color}
                        style={{ cursor: 'pointer' }}
                      />
                      {/* Value labels on important points - only show if not hovering */}
                      {isImportant && !hoveredPoint && (
                        <g>
                          <rect
                            x={p.x - 35}
                            y={p.y - 30}
                            width="70"
                            height="20"
                            fill="white"
                            stroke={color}
                            strokeWidth="2"
                            rx="4"
                            opacity="0.95"
                            style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}
                          />
                          <text
                            x={p.x}
                            y={p.y - 15}
                            textAnchor="middle"
                            fontSize="11"
                            fill={color}
                            fontWeight="700"
                          >
                            {formatValue(p.value)}
                          </text>
                        </g>
                      )}
                    </g>
                  );
                })}
              </g>
            );
          })}

          {/* X-axis labels - Show ALL names and dates horizontally */}
          {labels?.map((label, index) => {
            const totalLabels = labels.length;
            // Calculate x position with proper spacing
            const x = padding + (index * labelSpacing);
            const labelText = String(label);
            const individualDate = series?.[0]?.dates && series[0].dates[index] 
              ? formatDateByRange(series[0].dates[index], dateRangeType) 
              : null;
            
            return (
              <g key={index}>
                {/* Vertical line from axis */}
                <line 
                  x1={x} 
                  y1={svgChartHeight} 
                  x2={x} 
                  y2={svgChartHeight + 8} 
                  stroke="#424242" 
                  strokeWidth="2.5" 
                />
                {/* Name label - Horizontal text */}
                <text
                  x={x}
                  y={svgChartHeight + 22}
                  textAnchor="middle"
                  fontSize="12"
                  fill="#212121"
                  fontWeight="700"
                  style={{ 
                    textShadow: '1px 1px 2px white, -1px -1px 2px white',
                  }}
                >
                  {labelText}
                </text>
                {/* Date label below name - Show individual date if available, otherwise show range */}
                {individualDate ? (
                  <text
                    x={x}
                    y={svgChartHeight + 40}
                    textAnchor="middle"
                    fontSize="10"
                    fill="#757575"
                    fontWeight="500"
                    style={{ 
                      textShadow: '1px 1px 2px white',
                    }}
                  >
                    {individualDate}
                  </text>
                ) : startDate && endDate ? (
                  <text
                    x={x}
                    y={svgChartHeight + 40}
                    textAnchor="middle"
                    fontSize="9"
                    fill="#999"
                    fontWeight="400"
                    style={{ 
                      textShadow: '1px 1px 2px white',
                    }}
                  >
                    {formatDateByRange(startDate, dateRangeType)}
                  </text>
                ) : null}
              </g>
            );
          })}
          
          {/* Tooltip for hover */}
          {hoveredPoint && (
            <g>
              {/* Calculate tooltip dimensions and position */}
              {(() => {
                const tooltipWidth = 180;
                const tooltipHeight = hoveredPoint.date ? 70 : 55;
                const tooltipX = Math.min(
                  Math.max(tooltipPosition.x - tooltipWidth / 2, padding + 10),
                  containerWidth - tooltipWidth - 10
                );
                const tooltipY = Math.max(tooltipPosition.y - tooltipHeight - 15, padding + 10);
                
                return (
                  <>
                    {/* Tooltip background */}
                    <rect
                      x={tooltipX}
                      y={tooltipY}
                      width={tooltipWidth}
                      height={tooltipHeight}
                      fill="white"
                      stroke={hoveredPoint.color}
                      strokeWidth="2.5"
                      rx="8"
                      opacity="0.98"
                      style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.25))' }}
                    />
                    {/* Tooltip border accent */}
                    <rect
                      x={tooltipX}
                      y={tooltipY}
                      width={tooltipWidth}
                      height="4"
                      fill={hoveredPoint.color}
                      rx="8"
                    />
                    {/* Tooltip content */}
                    <text
                      x={tooltipX + tooltipWidth / 2}
                      y={tooltipY + 20}
                      textAnchor="middle"
                      fontSize="12"
                      fill="#212121"
                      fontWeight="700"
                    >
                      {hoveredPoint.label}
                    </text>
                    <text
                      x={tooltipX + tooltipWidth / 2}
                      y={tooltipY + 38}
                      textAnchor="middle"
                      fontSize="11"
                      fill={hoveredPoint.color}
                      fontWeight="700"
                    >
                      {hoveredPoint.seriesName}: {formatValue(hoveredPoint.value)}
                    </text>
                    {hoveredPoint.date && (
                      <text
                        x={tooltipX + tooltipWidth / 2}
                        y={tooltipY + 55}
                        textAnchor="middle"
                        fontSize="10"
                        fill="#757575"
                        fontWeight="500"
                      >
                        📅 {formatDateByRange(hoveredPoint.date, dateRangeType)}
                      </text>
                    )}
                    {/* Pointer line to point */}
                    <line
                      x1={tooltipX + tooltipWidth / 2}
                      y1={tooltipY + tooltipHeight}
                      x2={hoveredPoint.x}
                      y2={hoveredPoint.y}
                      stroke={hoveredPoint.color}
                      strokeWidth="2"
                      strokeDasharray="3,3"
                      opacity="0.4"
                    />
                    {/* Highlight circle on point */}
                    <circle
                      cx={hoveredPoint.x}
                      cy={hoveredPoint.y}
                      r={pointRadius + 5}
                      fill="none"
                      stroke={hoveredPoint.color}
                      strokeWidth="3"
                      opacity="0.6"
                      style={{ animation: 'pulse 1s infinite' }}
                    />
                  </>
                );
              })()}
            </g>
          )}
            </svg>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return <Typography>Unsupported chart type: {type}</Typography>;
};

const ConsultantPerformance = () => {
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [mainMetrics, setMainMetrics] = useState({});
  const [consultants, setConsultants] = useState([]);
  const [allConsultants, setAllConsultants] = useState([]); // All consultants for dropdown
  const [allColumns, setAllColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [customMetrics, setCustomMetrics] = useState(() => {
    // Initialize from localStorage if available, otherwise empty array
    try {
      const saved = localStorage.getItem('consultantPerformance_customMetrics');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    hasMore: false,
    nextCursor: null,
  });

  // Filters
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  // Initialize dates based on dateFilterType - if all_time, use very old date
  const [dateFilterType, setDateFilterType] = useState("all_time"); // last_7_days, last_10_days, last_30_days, today, custom, between, all_time
  const [startDate, setStartDate] = useState(() => {
    // For all_time on initial load, use very old date
    const veryOldDate = new Date('2000-01-01');
    veryOldDate.setUTCHours(0, 0, 0, 0);
    return veryOldDate;
  });
  const [endDate, setEndDate] = useState(new Date());
  const [customSingleDate, setCustomSingleDate] = useState(new Date());
  const [sortBy, setSortBy] = useState("revenuePerConsultant");
  const [sortDirection, setSortDirection] = useState("desc");

  // UI States
  const [columnsDialogOpen, setColumnsDialogOpen] = useState(false);
  const [customMetricDialogOpen, setCustomMetricDialogOpen] = useState(false);
  const [graphDialogOpen, setGraphDialogOpen] = useState(false);
  const [mainMetricsDialogOpen, setMainMetricsDialogOpen] = useState(false);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  
  // Selected Main Metrics (customizable)
  const [selectedMainMetrics, setSelectedMainMetrics] = useState(() => {
    try {
      const saved = localStorage.getItem('consultantPerformance_selectedMainMetrics');
      return saved ? JSON.parse(saved) : [
        'avgCancelledConsultationPercent',
        'avgMissedConsultationPercent',
        'freeConsultationsPerConsultant',
        'paidConsultationsPerConsultantPercent',
        'revenuePerConsultant',
        'avgOnlineTime',
        'avgBusyTime'
      ];
    } catch {
      return [
        'avgCancelledConsultationPercent',
        'avgMissedConsultationPercent',
        'freeConsultationsPerConsultant',
        'paidConsultationsPerConsultantPercent',
        'revenuePerConsultant',
        'avgOnlineTime',
        'avgBusyTime'
      ];
    }
  });

  // Custom Metric Builder
  const [newMetricName, setNewMetricName] = useState("");
  const [newMetricFormula, setNewMetricFormula] = useState("");
  const [editingMetricIndex, setEditingMetricIndex] = useState(null);
  const [selectedFieldForFormula, setSelectedFieldForFormula] = useState("");

  // Graph Builder
  const [selectedFields, setSelectedFields] = useState([]); // Multiple fields for graph
  const [graphType, setGraphType] = useState("area");
  const [graphData, setGraphData] = useState(null);
  const [chartData, setChartData] = useState({
    series: [],
    options: {
      chart: {
        type: "area",
        height: 350,
        toolbar: {
          show: false
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800
        }
      },
      colors: ["#3b82f6", "#ef4444", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899", "#06b6d4", "#f97316"],
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: "smooth",
        width: 3,
      },
      xaxis: {
        categories: [],
        labels: {
          style: {
            colors: '#666',
            fontSize: '12px',
            fontWeight: '500'
          }
        }
      },
      yaxis: {
        title: {
          text: "Value",
          style: {
            color: '#666',
            fontSize: '14px',
            fontWeight: '500'
          }
        },
        labels: {
          style: {
            colors: '#666',
            fontSize: '12px',
            fontWeight: '500'
          },
          formatter: function (val) {
            if (val === undefined || val === null || isNaN(val)) {
              return "0";
            }
            if (val >= 1000000) {
              return (val / 1000000).toFixed(1) + "M";
            } else if (val >= 1000) {
              return (val / 1000).toFixed(1) + "K";
            }
            return val.toFixed(0);
          }
        }
      },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.1,
          stops: [0, 90, 100],
        },
      },
      grid: {
        borderColor: '#f1f1f1',
        strokeDashArray: 4,
        xaxis: {
          lines: {
            show: true
          }
        },
        yaxis: {
          lines: {
            show: true
          }
        }
      },
      tooltip: {
        enabled: true,
        shared: true,
        intersect: false,
        custom: function({ series, seriesIndex, dataPointIndex, w }) {
          const category = w.globals.labels[dataPointIndex];
          let tooltipContent = `
            <div style="padding: 12px; background: #fff; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
              <div style="font-weight: 600; color: #242424; margin-bottom: 8px;">${category}</div>
          `;
          
          series.forEach((s, idx) => {
            const value = s[dataPointIndex];
            const seriesName = w.globals.seriesNames[idx];
            const seriesColor = w.globals.colors[idx] || '#3b82f6';
            tooltipContent += `
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                <span style="color: #666; display: flex; align-items: center;">
                  <span style="display: inline-block; width: 12px; height: 12px; background: ${seriesColor}; border-radius: 50%; margin-right: 8px;"></span>
                  ${seriesName}:
                </span>
                <span style="color: ${seriesColor}; font-weight: 600;">${value?.toLocaleString() || 0}</span>
              </div>
            `;
          });
          
          tooltipContent += `</div>`;
          return tooltipContent;
        }
      },
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'right',
        fontSize: '14px',
        fontWeight: 600,
        markers: {
          width: 12,
          height: 12,
          radius: 6
        }
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: {
              height: 300
            }
          }
        }
      ]
    },
  });
  const [graphLoading, setGraphLoading] = useState(false);
  const [selectedConsultantsForGraph, setSelectedConsultantsForGraph] = useState([]); // Selected consultants for graph
  const [consultantSearchText, setConsultantSearchText] = useState(""); // Search text for consultant selection
  
  // Group comparison states
  const [graphMode, setGraphMode] = useState("individual"); // "individual" or "group"
  const [groups, setGroups] = useState([
    { id: 1, name: "Group 1", consultants: [] },
    { id: 2, name: "Group 2", consultants: [] }
  ]);
  const [selectedGroupsForComparison, setSelectedGroupsForComparison] = useState([]); // Selected group IDs for comparison
  
  // Graph-specific date filter (separate from main table filter)
  const [graphDateFilterType, setGraphDateFilterType] = useState("all_time");
  const [graphStartDate, setGraphStartDate] = useState(() => {
    const veryOldDate = new Date('2000-01-01');
    veryOldDate.setUTCHours(0, 0, 0, 0);
    return veryOldDate;
  });
  const [graphEndDate, setGraphEndDate] = useState(new Date());
  const [graphCustomSingleDate, setGraphCustomSingleDate] = useState(new Date());

  // Initialize columns
  useEffect(() => {
    const defaultColumns = [
      "name",
      "attemptedCallConsultation",
      "attemptedChatConsultation",
      "missedCallConsultation",
      "missedChatConsultation",
      "canceledCallConsultation",
      "canceledChatConsultation",
      "freeChatConsultation",
      "freeCallConsultation",
      "paidChatConsultation1",
      "paidCallConsultation1",
      "fixedSessions",
      "revenueFromChat",
      "revenueFromCall",
      "revenueFromFixedSession",
      "giftPerConsultant",
      "revenuePerConsultant",
      "onlineTime",
      "chatTime",
      "callTime",
      "ratingByUser",
      "followers",
      "profileView",
    ];
    setSelectedColumns(defaultColumns);
  }, []);

  // Fetch data - OPTIMIZED: Load main metrics and list in parallel
  const fetchData = useCallback(async () => {
    try {
      showLoader();
      // Ensure customMetrics is always an array
      const safeCustomMetrics = Array.isArray(customMetrics) ? customMetrics : [];
      // Ensure selectedColumns is always an array
      const safeColumns = Array.isArray(selectedColumns) ? selectedColumns : [];
      
      const baseParams = {
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        allTime: dateFilterType === "all_time" ? "true" : "false",
        customMetrics: JSON.stringify(safeCustomMetrics), // Include custom metrics in main metrics API
      };

      // OPTIMIZED: Fetch main metrics and list in parallel
      const [mainMetricsResponse, listResponse] = await Promise.all([
        fetchMainMetricsApi(baseParams),
        (async () => {
          const listParams = {
            ...baseParams,
            search: debouncedSearch,
            sortBy,
            sortDirection,
            page: pagination.page,
            limit: pagination.limit,
            columns: JSON.stringify(safeColumns),
            customMetrics: JSON.stringify(safeCustomMetrics),
          };

          if (pagination.nextCursor && pagination.page > 1) {
            listParams.cursor = pagination.nextCursor;
          }

          return fetchConsultantListApi(listParams);
        })()
      ]);

      // Handle main metrics response
      if (mainMetricsResponse?.data?.code === 200) {
        setMainMetrics(mainMetricsResponse.data.result || {});
      } else if (mainMetricsResponse?.data?.code) {
        console.error("Error fetching main metrics:", mainMetricsResponse.data.message);
      }

      // Handle list response
      if (listResponse?.data?.code === 200) {
        setConsultants(listResponse.data.result.consultants || []);
        setAllColumns(listResponse.data.result.allColumns || []);
        setPagination(listResponse.data.result.pagination || pagination);
        scrollToUp();
      } else if (listResponse?.data?.code) {
        toastMessage(listResponse.data.message || "Failed to fetch consultant list", "error");
      }
    } catch (error) {
      console.error("Error fetching consultant performance:", error);
      toastMessage("Failed to fetch consultant performance data", "error");
    } finally {
      hideLoader();
    }
  }, [
    startDate,
    endDate,
    debouncedSearch,
    sortBy,
    sortDirection,
    pagination.page,
    pagination.limit,
    selectedColumns,
    customMetrics,
    dateFilterType,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch all consultants for dropdown (without pagination) - OPTIMIZED
  useEffect(() => {
    const fetchAllConsultants = async () => {
      try {
        const safeCustomMetrics = Array.isArray(customMetrics) ? customMetrics : [];
        
        const params = {
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0],
          search: "", // No search filter for dropdown
          sortBy: "name",
          sortDirection: "asc",
          page: 1,
          limit: 10000, // Large limit to get all consultants
          columns: JSON.stringify(["name"]), // Only need name column
          customMetrics: JSON.stringify(safeCustomMetrics),
          allTime: dateFilterType === "all_time" ? "true" : "false",
        };

        const { data } = await fetchConsultantListApi(params);
        if (data.code === 200) {
          setAllConsultants(data.result.consultants || []);
        }
      } catch (error) {
        console.error("Error fetching all consultants:", error);
      }
    };

    fetchAllConsultants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, dateFilterType]);

  // Handle column toggle
  const handleColumnToggle = (column) => {
    setSelectedColumns((prev) =>
      prev.includes(column)
        ? prev.filter((c) => c !== column)
        : [...prev, column]
    );
  };

  // Add custom metric
  const handleAddCustomMetric = () => {
    if (!newMetricName || !newMetricFormula) {
      toastMessage("Please provide both name and formula", "error");
      return;
    }

    const newMetric = {
      name: newMetricName,
      formula: newMetricFormula,
    };

    let updatedMetrics;
    if (editingMetricIndex !== null) {
      // Edit existing metric
      updatedMetrics = [...customMetrics];
      updatedMetrics[editingMetricIndex] = newMetric;
      toastMessage("Custom metric updated successfully", "success");
    } else {
      // Add new metric
      updatedMetrics = Array.isArray(customMetrics) 
        ? [...customMetrics, newMetric] 
        : [newMetric];
      toastMessage("Custom metric added successfully", "success");
    }
    
    setCustomMetrics(updatedMetrics);
    // Save to localStorage
    try {
      localStorage.setItem('consultantPerformance_customMetrics', JSON.stringify(updatedMetrics));
    } catch (e) {
      console.error('Failed to save custom metrics to localStorage:', e);
    }
    
    setNewMetricName("");
    setNewMetricFormula("");
    setEditingMetricIndex(null);
    setSelectedFieldForFormula("");
    setCustomMetricDialogOpen(false);
    fetchData();
  };

  // Handle main metrics toggle
  const handleMainMetricToggle = (metricKey) => {
    setSelectedMainMetrics((prev) => {
      const updated = prev.includes(metricKey)
        ? prev.filter((key) => key !== metricKey)
        : [...prev, metricKey];
      // Save to localStorage
      try {
        localStorage.setItem('consultantPerformance_selectedMainMetrics', JSON.stringify(updated));
      } catch (e) {
        console.error('Failed to save selected main metrics to localStorage:', e);
      }
      return updated;
    });
  };

  // Edit custom metric
  const handleEditCustomMetric = (index) => {
    const metric = customMetrics[index];
    setNewMetricName(metric.name);
    setNewMetricFormula(metric.formula);
    setEditingMetricIndex(index);
    setCustomMetricDialogOpen(true);
  };

  // Add field to formula
  const handleAddFieldToFormula = () => {
    if (selectedFieldForFormula) {
      setNewMetricFormula(prev => {
        const current = prev || "";
        return current ? `${current} ${selectedFieldForFormula}` : selectedFieldForFormula;
      });
      setSelectedFieldForFormula("");
    }
  };

  // Remove custom metric
  const handleRemoveCustomMetric = (index) => {
    const updatedMetrics = Array.isArray(customMetrics) 
      ? customMetrics.filter((_, i) => i !== index)
      : [];
    
    setCustomMetrics(updatedMetrics);
    // Save to localStorage
    try {
      localStorage.setItem('consultantPerformance_customMetrics', JSON.stringify(updatedMetrics));
    } catch (e) {
      console.error('Failed to save custom metrics to localStorage:', e);
    }
  };

  // Fetch graph data
  // Filter graph data based on selected consultants
  const filterGraphDataByConsultants = (data) => {
    if (!data || !data.labels || !data.series) return data;
    if (selectedConsultantsForGraph.length === 0) return data; // Show all if none selected
    
    // Check if this is time-series data (dates as labels) vs consultant-wise data
    // Time-series data has date-like labels (e.g., "Jan 1", "Dec 10", "2024", etc.)
    const isTimeSeriesData = data.labels.some(label => {
      const labelStr = String(label || '');
      // Check for date patterns: month names, year-only, or date formats
      const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
      const hasMonthName = monthNames.some(month => labelStr.toLowerCase().includes(month));
      const isYearOnly = /^\d{4}$/.test(labelStr.trim()); // Just year like "2024"
      const isDatePattern = /^\d{1,2}\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i.test(labelStr);
      
      return hasMonthName || isYearOnly || isDatePattern;
    });
    
    // For time-series data, don't filter by consultants (labels are dates, not consultant names)
    if (isTimeSeriesData) {
      return data;
    }
    
    // For consultant-wise data, apply filtering
    const filteredLabels = [];
    const filteredSeries = data.series.map(series => ({
      ...series,
      data: []
    }));
    
    // Filter labels and corresponding data points
    data.labels.forEach((label, index) => {
      // Check if this label (consultant name) is in selected consultants
      const isSelected = selectedConsultantsForGraph.some(selected => {
        if (!selected) return false; // Skip null/undefined
        const selectedName = typeof selected === 'string' ? selected : selected?.name || selected;
        if (!selectedName || !label) return false; // Safety check
        return String(label).toLowerCase().includes(String(selectedName).toLowerCase()) ||
               String(selectedName).toLowerCase().includes(String(label).toLowerCase());
      });
      
      if (isSelected) {
        filteredLabels.push(label);
        // Add corresponding data point for each series
        data.series.forEach((series, seriesIndex) => {
          if (series.data && series.data[index] !== undefined) {
            filteredSeries[seriesIndex].data.push(series.data[index]);
          }
        });
      }
    });
    
    return {
      ...data,
      labels: filteredLabels,
      series: filteredSeries
    };
  };

  const handleFetchGraph = async () => {
    if (selectedFields.length > 0) {
      try {
        setGraphLoading(true);
        
        // Use graph-specific date filter (not the main table filter)
        let graphStartDateValue = graphStartDate;
        let graphEndDateValue = graphEndDate;
        
        if (graphDateFilterType === "all_time") {
          // For all time, start from 2025-01-01 to show only 2025 data
          graphStartDateValue = new Date('2025-01-01'); // Start from 2025
          graphEndDateValue = new Date(); // Today
        } else {
          // Validate dates for other filter types
          if (!graphStartDate || !graphEndDate) {
            toastMessage("Please select valid start and end dates", "error");
            setGraphLoading(false);
            return;
          }
          
          if (graphStartDate > graphEndDate) {
            toastMessage("Start date cannot be after end date", "error");
            setGraphLoading(false);
            return;
          }
        }
        
        // Check graph mode and selected items
        const isGroupMode = graphMode === "group";
        const hasSelectedGroups = isGroupMode && selectedGroupsForComparison && selectedGroupsForComparison.length > 0;
        const hasSelectedConsultants = !isGroupMode && selectedConsultantsForGraph && selectedConsultantsForGraph.length > 0;
        
        // For group mode, validate groups are selected
        if (isGroupMode && !hasSelectedGroups) {
          toastMessage("Please select at least one group to compare", "warning");
          setGraphLoading(false);
          return;
        }
        
        // For individual mode, validate consultants are selected
        if (!isGroupMode && !hasSelectedConsultants) {
          toastMessage("Please select at least one consultant to display in the graph", "warning");
          setGraphLoading(false);
          return;
        }
        
        // Fetch time-series data
        console.log("Fetching time-series graph data:", {
          graphMode: isGroupMode ? "group" : "individual",
          selectedFields,
          selectedConsultants: selectedConsultantsForGraph,
          selectedGroups: selectedGroupsForComparison,
          hasSelectedConsultants,
          hasSelectedGroups,
          graphDateFilterType,
          graphStartDate: graphStartDateValue.toISOString().split('T')[0],
          graphEndDate: graphEndDateValue.toISOString().split('T')[0],
          allTime: graphDateFilterType === "all_time"
        });
        
        let timeSeriesResults = [];
        
        // Group comparison mode
        if (isGroupMode && hasSelectedGroups) {
          // Get selected groups
          const selectedGroups = groups.filter(g => selectedGroupsForComparison.includes(g.id));
          
          // Fetch data for each group + metric combination
          const groupMetricPromises = [];
          
          selectedGroups.forEach((group) => {
            selectedFields.forEach((metric) => {
              // Fetch data for each consultant in the group, then aggregate
              // Filter out undefined, null, or empty consultant names
              const validConsultants = group.consultants.filter(c => {
                const consultantName = typeof c === 'string' ? c : c?.name || c;
                return consultantName && consultantName !== 'undefined' && consultantName !== 'null' && consultantName.trim() !== '';
              });
              
              const consultantPromises = validConsultants.map(async (consultantName) => {
                try {
                  const params = {
                    metric: metric,
                    graphType,
                    startDate: graphStartDateValue.toISOString().split('T')[0],
                    endDate: graphEndDateValue.toISOString().split('T')[0],
                    allTime: graphDateFilterType === "all_time" ? "true" : "false",
                    consultantName: typeof consultantName === 'string' ? consultantName : consultantName?.name || consultantName,
                  };
                  
                  const response = await fetchConsultantPerformanceGraphApi(params);
                  const data = response?.data || response;
                  
                  if (data && (data.code === 200 || data.success === true)) {
                    return {
                      consultantName: typeof consultantName === 'string' ? consultantName : consultantName?.name || consultantName,
                      result: data.result || {}
                    };
                  }
                  return null;
                } catch (error) {
                  console.error(`Error fetching data for ${metric} - ${consultantName}:`, error);
                  return null;
                }
              });
              
              groupMetricPromises.push({
                groupId: group.id,
                groupName: group.name,
                metric,
                promise: Promise.all(consultantPromises)
              });
            });
          });
          
          // Execute all promises
          const groupResults = await Promise.all(groupMetricPromises.map(item => 
            item.promise.then(results => ({
              groupId: item.groupId,
              groupName: item.groupName,
              metric: item.metric,
              consultantResults: results.filter(r => r != null)
            }))
          ));
          
          // Group results by metric, then aggregate by group
          const groupedByMetric = {};
          groupResults.forEach((result) => {
            if (!groupedByMetric[result.metric]) {
              groupedByMetric[result.metric] = [];
            }
            groupedByMetric[result.metric].push({
              groupId: result.groupId,
              groupName: result.groupName,
              consultantResults: result.consultantResults
            });
          });
          
          // Convert to array format
          timeSeriesResults = selectedFields.map(metric => {
            const groupData = groupedByMetric[metric] || [];
            if (groupData.length > 0) {
              return {
                metric,
                groupData
              };
            }
            return null;
          }).filter(r => r != null);
        } else if (hasSelectedConsultants) {
          // Fetch data for each consultant + metric combination
          const consultantMetricPromises = [];
          
          selectedConsultantsForGraph.forEach((consultantName) => {
            selectedFields.forEach((metric) => {
              consultantMetricPromises.push({
                consultantName: typeof consultantName === 'string' ? consultantName : consultantName?.name || consultantName,
                metric,
                promise: (async () => {
                  try {
                    const params = {
                      metric: metric,
                      graphType,
                      startDate: graphStartDateValue.toISOString().split('T')[0],
                      endDate: graphEndDateValue.toISOString().split('T')[0],
                      allTime: graphDateFilterType === "all_time" ? "true" : "false",
                      consultantName: typeof consultantName === 'string' ? consultantName : consultantName?.name || consultantName, // Pass consultant name
                    };
                    
                    const response = await fetchConsultantPerformanceGraphApi(params);
                    const data = response?.data || response;
                    
                    if (data && (data.code === 200 || data.success === true)) {
                      return {
                        consultantName: typeof consultantName === 'string' ? consultantName : consultantName?.name || consultantName,
                        metric,
                        result: data.result || {}
                      };
                    }
                    return null;
                  } catch (error) {
                    console.error(`Error fetching data for ${metric} - ${consultantName}:`, error);
                    return null;
                  }
                })()
              });
            });
          });
          
          // Execute all promises
          const results = await Promise.all(consultantMetricPromises.map(item => item.promise));
          
          // Group results by metric, then by consultant
          const groupedByMetric = {};
          results.forEach((result, index) => {
            if (result) {
              const { consultantName, metric, result: data } = result;
              
              // Skip if consultantName is undefined, null, or empty
              if (!consultantName || consultantName === 'undefined' || consultantName === 'null') {
                return;
              }
              
              if (!groupedByMetric[metric]) {
                groupedByMetric[metric] = [];
              }
              groupedByMetric[metric].push({
                consultantName,
                data
              });
            }
          });
          
          // Convert to array format similar to original
          timeSeriesResults = selectedFields.map(metric => {
            const consultantData = groupedByMetric[metric] || [];
            if (consultantData.length > 0) {
              // Combine all consultant data for this metric
              // We'll create separate series for each consultant later
              return {
                metric,
                consultantData
              };
            }
            return null;
          }).filter(r => r != null);
        } else {
          // Original logic: fetch aggregated data for all consultants
          const timeSeriesPromises = selectedFields.map(async (metric) => {
            try {
              const params = {
                metric: metric,
                graphType,
                startDate: graphStartDateValue.toISOString().split('T')[0],
                endDate: graphEndDateValue.toISOString().split('T')[0],
                allTime: graphDateFilterType === "all_time" ? "true" : "false",
              };
              
              const response = await fetchConsultantPerformanceGraphApi(params);
              const data = response?.data || response;
              
              if (data && (data.code === 200 || data.success === true)) {
                return {
                  metric,
                  result: data.result || {}
                };
              }
              return null;
            } catch (error) {
              console.error(`Error fetching data for metric ${metric}:`, error);
              return null;
            }
          });
          
          timeSeriesResults = await Promise.all(timeSeriesPromises);
        }
        
        // Filter out null results
        const validResults = timeSeriesResults.filter(r => r != null);
        
        console.log("Valid results after filtering:", {
          validResultsCount: validResults.length,
          hasSelectedConsultants,
          results: validResults.map((r, idx) => ({
            index: idx,
            metric: r.metric,
            hasConsultantData: !!r.consultantData,
            consultantCount: r.consultantData?.length || 0,
            labelsCount: r.result?.labels?.length || r.consultantData?.[0]?.data?.labels?.length || 0
          }))
        });
        
        if (validResults.length === 0) {
          toastMessage("No data available for the selected criteria. Please try different dates or fields.", "warning");
          setGraphData(null);
          setChartData({
            series: [],
            options: {
              ...chartData.options,
              xaxis: {
                ...chartData.options.xaxis,
                categories: []
              }
            }
          });
          setGraphLoading(false);
          return;
        }
        
        // Combine all time-series data
        const daysDiff = Math.ceil((graphEndDateValue - graphStartDateValue) / (1000 * 60 * 60 * 24));
        let baseLabels = [];
        let combinedSeries = [];
        
        // For "all_time" filter, show month-wise from 2025 onwards
        // For other filters (last 7, 10, 30, custom), show date-wise
        if (graphDateFilterType === "all_time") {
          // Generate all months from 2025-01-01 to current month
          const allMonths = [];
          const startMonth = new Date(2025, 0, 1); // January 2025
          const today = new Date();
          const endMonth = new Date(today.getFullYear(), today.getMonth(), 1); // Current month only
          
          let currentMonth = new Date(startMonth);
          const monthSet = new Set(); // To prevent duplicates
          
          while (currentMonth <= endMonth) {
            // Only include months from 2025 onwards and up to current month (not future months)
            if (currentMonth.getFullYear() >= 2025) {
              if (currentMonth.getFullYear() < today.getFullYear() || 
                  (currentMonth.getFullYear() === today.getFullYear() && currentMonth.getMonth() <= today.getMonth())) {
                const monthKey = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;
                
                // Prevent duplicate months
                if (!monthSet.has(monthKey)) {
                  monthSet.add(monthKey);
                  const monthLabel = currentMonth.toLocaleDateString('en-US', { month: 'long' }); // "January", "February", etc. (no year since all are 2025)
                  allMonths.push({
                    key: monthKey,
                    label: monthLabel,
                    year: currentMonth.getFullYear(),
                    month: currentMonth.getMonth(),
                    backendLabel: currentMonth.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) // "Jan 2025" format for matching
                  });
                }
              }
            }
            currentMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
          }
          
          baseLabels = allMonths.map(m => m.label);
          
          // Map backend data to our generated month labels
          combinedSeries = [];
          
          validResults.forEach((result) => {
            const metricName = result.metric || selectedFields[0];
            
            // Group comparison mode
            if (isGroupMode && result.groupData) {
              result.groupData.forEach((groupItem) => {
                const groupName = groupItem.groupName;
                const consultantResults = groupItem.consultantResults || [];
                
                // Aggregate data from all consultants in the group
                const aggregatedData = allMonths.map(monthInfo => {
                  let totalValue = 0;
                  
                  consultantResults.forEach((consultantItem) => {
                    const consultantResult = consultantItem.result;
                    const resultLabels = (consultantResult?.labels || []).map(l => String(l).trim());
                    const resultData = consultantResult?.series?.[0]?.data || [];
                    
                    // Find matching label index
                    let labelIndex = -1;
                    labelIndex = resultLabels.findIndex(l => l === monthInfo.backendLabel);
                    if (labelIndex < 0) {
                      labelIndex = resultLabels.findIndex(l => l === monthInfo.key);
                    }
                    if (labelIndex < 0) {
                      const monthNameShort = monthInfo.backendLabel.split(' ')[0];
                      labelIndex = resultLabels.findIndex(l => {
                        const lStr = String(l).trim();
                        return lStr.includes(monthNameShort) && lStr.includes(String(monthInfo.year));
                      });
                    }
                    
                    if (labelIndex >= 0 && labelIndex < resultData.length) {
                      const value = resultData[labelIndex];
                      totalValue += typeof value === 'number' ? value : (parseFloat(value) || 0);
                    }
                  });
                  
                  return totalValue;
                });
                
                const metricDisplayName = consultantResults[0]?.result?.series?.[0]?.name || metricName;
                combinedSeries.push({
                  name: `${metricDisplayName} - ${groupName}`,
                  data: aggregatedData,
                  dates: allMonths.map(m => m.key)
                });
              });
            } else if (hasSelectedConsultants && result.consultantData) {
              // Create separate series for each consultant
              result.consultantData.forEach((consultantItem) => {
                const consultantName = consultantItem.consultantName;
                
                // Skip if consultantName is undefined, null, or empty
                if (!consultantName || consultantName === 'undefined' || consultantName === 'null') {
                  return;
                }
                
                const consultantResult = consultantItem.data;
                const metricDisplayName = consultantResult?.series?.[0]?.name || metricName;
                const resultLabels = (consultantResult?.labels || []).map(l => String(l).trim());
                const resultData = consultantResult?.series?.[0]?.data || [];
                
                // Map data to our generated month labels
                const monthlyData = allMonths.map(monthInfo => {
                  let labelIndex = -1;
                  
                  // Try exact match with backend label format "Jan 2025"
                  labelIndex = resultLabels.findIndex(l => l === monthInfo.backendLabel);
                  
                  // Try match with key format "2025-01"
                  if (labelIndex < 0) {
                    labelIndex = resultLabels.findIndex(l => l === monthInfo.key);
                  }
                  
                  // Try partial match (month name + year)
                  if (labelIndex < 0) {
                    const monthNameShort = monthInfo.backendLabel.split(' ')[0];
                    labelIndex = resultLabels.findIndex(l => {
                      const lStr = String(l).trim();
                      return lStr.includes(monthNameShort) && lStr.includes(String(monthInfo.year));
                    });
                  }
                  
                  if (labelIndex >= 0 && labelIndex < resultData.length) {
                    const value = resultData[labelIndex];
                    return typeof value === 'number' ? value : (parseFloat(value) || 0);
                  }
                  return 0;
                });
                
                combinedSeries.push({
                  name: `${metricDisplayName} - ${consultantName}`,
                  data: monthlyData,
                  dates: allMonths.map(m => m.key)
                });
              });
            } else {
              // Original logic: aggregated data for all consultants
              const metricDisplayName = result?.result?.series?.[0]?.name || result?.series?.[0]?.name || metricName;
              const resultLabels = ((result?.result?.labels || result?.labels) || []).map(l => String(l).trim());
              const resultData = (result?.result?.series?.[0]?.data || result?.series?.[0]?.data) || [];
              
              // Map data to our generated month labels
              const monthlyData = allMonths.map(monthInfo => {
                let labelIndex = -1;
                
                // Try exact match with backend label format "Jan 2025"
                labelIndex = resultLabels.findIndex(l => l === monthInfo.backendLabel);
                
                // Try match with key format "2025-01"
                if (labelIndex < 0) {
                  labelIndex = resultLabels.findIndex(l => l === monthInfo.key);
                }
                
                // Try partial match (month name + year)
                if (labelIndex < 0) {
                  const monthNameShort = monthInfo.backendLabel.split(' ')[0];
                  labelIndex = resultLabels.findIndex(l => {
                    const lStr = String(l).trim();
                    return lStr.includes(monthNameShort) && lStr.includes(String(monthInfo.year));
                  });
                }
                
                if (labelIndex >= 0 && labelIndex < resultData.length) {
                  const value = resultData[labelIndex];
                  return typeof value === 'number' ? value : (parseFloat(value) || 0);
                }
                return 0;
              });
              
              combinedSeries.push({
                name: metricDisplayName,
                data: monthlyData,
                dates: allMonths.map(m => m.key)
              });
            }
          });
        } else {
          // For other filters (last 7, 10, 30, custom dates), show date-wise
          const dateLabels = [];
          const dateObjects = [];
          
          for (let i = 0; i <= daysDiff; i++) {
            const date = new Date(graphStartDateValue);
            date.setDate(date.getDate() + i);
            dateObjects.push(date);
            
            // Format date label based on date range type
            const dateRangeType = daysDiff <= 1 ? 'hour' : daysDiff <= 7 ? 'day' : 'day';
            
            let label = '';
            if (dateRangeType === 'hour') {
              label = date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit' });
            } else {
              label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            }
            dateLabels.push(label);
          }
          
          baseLabels = dateLabels;
          
          combinedSeries = [];
          
          validResults.forEach((result) => {
            const metricName = result.metric || selectedFields[0];
            
            // Group comparison mode
            if (isGroupMode && result.groupData) {
              result.groupData.forEach((groupItem) => {
                const groupName = groupItem.groupName;
                const consultantResults = groupItem.consultantResults || [];
                
                // Aggregate data from all consultants in the group
                const aggregatedData = dateObjects.map((dateObj, dateIndex) => {
                  const dateLabel = dateLabels[dateIndex];
                  let totalValue = 0;
                  
                  consultantResults.forEach((consultantItem) => {
                    const consultantResult = consultantItem.result;
                    const resultLabels = consultantResult?.labels || [];
                    const resultData = consultantResult?.series?.[0]?.data || [];
                    
                    // Try exact match first
                    let labelIndexInResult = resultLabels.findIndex(l => l === dateLabel);
                    
                    // If no exact match, try to find by date
                    if (labelIndexInResult < 0) {
                      labelIndexInResult = resultLabels.findIndex(resultLabel => {
                        const currentYear = dateObj.getFullYear();
                        const dateMatch = resultLabel.match(/(\w+)\s+(\d+)/);
                        if (dateMatch) {
                          const monthName = dateMatch[1];
                          const day = parseInt(dateMatch[2]);
                          const monthMap = {
                            'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
                            'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
                          };
                          const month = monthMap[monthName];
                          if (month !== undefined) {
                            const resultDate = new Date(currentYear, month, day);
                            return resultDate.getTime() === dateObj.getTime();
                          }
                        }
                        return false;
                      });
                    }
                    
                    if (labelIndexInResult >= 0 && labelIndexInResult < resultData.length) {
                      totalValue += resultData[labelIndexInResult] || 0;
                    }
                  });
                  
                  return totalValue;
                });
                
                const dates = dateObjects.map(dateObj => dateObj.toISOString());
                const metricDisplayName = consultantResults[0]?.result?.series?.[0]?.name || metricName;
                
                combinedSeries.push({
                  name: `${metricDisplayName} - ${groupName}`,
                  data: aggregatedData,
                  dates: dates
                });
              });
            } else if (hasSelectedConsultants && result.consultantData) {
              // Create separate series for each consultant
              result.consultantData.forEach((consultantItem) => {
                const consultantName = consultantItem.consultantName;
                
                // Skip if consultantName is undefined, null, or empty
                if (!consultantName || consultantName === 'undefined' || consultantName === 'null') {
                  return;
                }
                
                const consultantResult = consultantItem.data;
                const metricDisplayName = consultantResult?.series?.[0]?.name || metricName;
                const resultLabels = consultantResult?.labels || [];
                const resultData = consultantResult?.series?.[0]?.data || [];
                
                // Align data with base labels (dates) by matching date objects
                const alignedData = dateObjects.map((dateObj, dateIndex) => {
                  const dateLabel = dateLabels[dateIndex];
                  
                  // Try exact match first
                  let labelIndexInResult = resultLabels.findIndex(l => l === dateLabel);
                  
                  // If no exact match, try to find by date
                  if (labelIndexInResult < 0) {
                    labelIndexInResult = resultLabels.findIndex(resultLabel => {
                      const currentYear = dateObj.getFullYear();
                      const dateMatch = resultLabel.match(/(\w+)\s+(\d+)/);
                      if (dateMatch) {
                        const monthName = dateMatch[1];
                        const day = parseInt(dateMatch[2]);
                        const monthMap = {
                          'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
                          'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
                        };
                        const month = monthMap[monthName];
                        if (month !== undefined) {
                          const resultDate = new Date(currentYear, month, day);
                          return resultDate.getTime() === dateObj.getTime();
                        }
                      }
                      return false;
                    });
                  }
                  
                  if (labelIndexInResult >= 0 && labelIndexInResult < resultData.length) {
                    return resultData[labelIndexInResult] || 0;
                  }
                  return 0;
                });
                
                const dates = dateObjects.map(dateObj => dateObj.toISOString());
                
                combinedSeries.push({
                  name: `${metricDisplayName} - ${consultantName}`,
                  data: alignedData,
                  dates: dates
                });
              });
            } else {
              // Original logic: aggregated data for all consultants
              const metricDisplayName = result?.result?.series?.[0]?.name || result?.series?.[0]?.name || metricName;
              const resultLabels = result?.result?.labels || result?.labels || [];
              const resultData = result?.result?.series?.[0]?.data || result?.series?.[0]?.data || [];
              
              // Align data with base labels (dates) by matching date objects
              const alignedData = dateObjects.map((dateObj, dateIndex) => {
                const dateLabel = dateLabels[dateIndex];
                
                // Try exact match first
                let labelIndexInResult = resultLabels.findIndex(l => l === dateLabel);
                
                // If no exact match, try to find by date
                if (labelIndexInResult < 0) {
                  labelIndexInResult = resultLabels.findIndex(resultLabel => {
                    const currentYear = dateObj.getFullYear();
                    const dateMatch = resultLabel.match(/(\w+)\s+(\d+)/);
                    if (dateMatch) {
                      const monthName = dateMatch[1];
                      const day = parseInt(dateMatch[2]);
                      const monthMap = {
                        'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
                        'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
                      };
                      const month = monthMap[monthName];
                      if (month !== undefined) {
                        const resultDate = new Date(currentYear, month, day);
                        return resultDate.getTime() === dateObj.getTime();
                      }
                    }
                    return false;
                  });
                }
                
                if (labelIndexInResult >= 0 && labelIndexInResult < resultData.length) {
                  return resultData[labelIndexInResult] || 0;
                }
                return 0;
              });
              
              const dates = dateObjects.map(dateObj => dateObj.toISOString());
              
              combinedSeries.push({
                name: metricDisplayName,
                data: alignedData,
                dates: dates
              });
            }
          });
        }
        
        // Filter out any series with "undefined" in the name
        const filteredSeries = combinedSeries.filter(series => {
          const seriesName = series?.name || '';
          return !seriesName.includes('undefined') && !seriesName.includes(' - undefined');
        });
        
        const timeSeriesResult = {
          labels: baseLabels, // Dates
          series: filteredSeries
        };
        
        // Note: For time-series data, consultant filtering is not applicable
        // The data is aggregated across all consultants for each date
        setGraphData(timeSeriesResult);
        
        // Update chartData in dashboard format
        const apexSeries = filteredSeries.map((s) => ({
          name: s.name || "Series",
          data: s.data || []
        }));
        
        console.log("Setting chart data:", {
          seriesCount: apexSeries.length,
          series: apexSeries.map(s => ({
            name: s.name,
            dataLength: s.data.length,
            dataSample: s.data.slice(0, 5),
            total: s.data.reduce((sum, val) => sum + val, 0)
          })),
          baseLabelsCount: baseLabels.length,
          baseLabelsSample: baseLabels.slice(0, 10)
        });
        
        // Ensure we have valid data before setting chart
        if (apexSeries.length > 0 && baseLabels.length > 0) {
          setChartData({
            series: apexSeries,
            options: {
              ...chartData.options,
              chart: {
                ...chartData.options.chart,
                type: graphType === "bar" ? "bar" : graphType === "line" ? "line" : "area"
              },
              xaxis: {
                ...chartData.options.xaxis,
                categories: baseLabels,
                labels: {
                  ...chartData.options.xaxis.labels,
                  rotate: graphDateFilterType === "all_time" && baseLabels.length > 12 ? -45 : 0,
                  rotateAlways: graphDateFilterType === "all_time" && baseLabels.length > 12,
                  style: {
                    ...chartData.options.xaxis.labels?.style,
                    fontSize: graphDateFilterType === "all_time" && baseLabels.length > 24 ? '10px' : '12px'
                  }
                }
              },
              yaxis: {
                ...chartData.options.yaxis,
                title: {
                  ...chartData.options.yaxis.title,
                  text: selectedFields.length > 0 
                    ? selectedFields.map(f => getColumnDisplayName(f)).join(" & ")
                    : "Value"
                }
              },
              fill: {
                type: graphType === "bar" ? "solid" : "gradient",
                gradient: graphType === "bar" ? undefined : {
                  shadeIntensity: 1,
                  opacityFrom: 0.7,
                  opacityTo: 0.1,
                  stops: [0, 90, 100],
                }
              },
              stroke: {
                curve: "smooth",
                width: graphType === "bar" ? 0 : 3
              }
            }
          });
        } else {
          console.warn("No valid chart data to display:", {
            apexSeriesLength: apexSeries.length,
            baseLabelsLength: baseLabels.length
          });
          setChartData({
            series: [],
            options: {
              ...chartData.options,
              xaxis: {
                ...chartData.options.xaxis,
                categories: []
              }
            }
          });
        }
        
        setGraphDialogOpen(false); // Close modal after generating graph
      } catch (error) {
        console.error("Error fetching graph data:", error);
        console.error("Error details:", {
          message: error?.message,
          response: error?.response?.data,
          status: error?.response?.status,
          config: error?.config
        });
        
        let errorMessage = "Failed to fetch graph data. ";
        if (error?.response?.data?.message) {
          errorMessage += error.response.data.message;
        } else if (error?.message) {
          errorMessage += error.message;
        } else if (error?.response?.status === 401) {
          errorMessage += "Please login again.";
        } else if (error?.response?.status === 403) {
          errorMessage += "You don't have permission to access this data.";
        } else if (error?.response?.status >= 500) {
          errorMessage += "Server error. Please try again later.";
        } else {
          errorMessage += "Please check your network connection and try again.";
        }
        
        toastMessage(errorMessage, "error");
        setGraphData(null);
        setChartData({
          series: [],
          options: {
            ...chartData.options,
            xaxis: {
              ...chartData.options.xaxis,
              categories: []
            }
          }
        });
      } finally {
        setGraphLoading(false);
      }
    } else {
      toastMessage("Please select at least one field to display", "error");
    }
  };


  // Format number
  const formatNumber = (num) => {
    if (num === null || num === undefined) return "0";
    if (typeof num !== "number") return num;
    // Format with locale string, then remove .00 if present
    const formatted = num.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    // Remove .00 at the end
    return formatted.replace(/\.00$/, '');
  };

  // Format time (seconds to hours)
  const formatTime = (seconds) => {
    if (!seconds) return "0h";
    const hours = seconds / 3600;
    return `${hours.toFixed(2)}h`;
  };

  // Smart date formatting based on date range
  const getDateRangeFormat = (start, end) => {
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 1) {
      return 'hour'; // Show hours for same day or 1 day
    } else if (diffDays <= 7) {
      return 'day'; // Show days for up to a week
    } else if (diffDays <= 31) {
      return 'day'; // Show days for up to a month
    } else if (diffDays <= 365) {
      return 'month'; // Show months for up to a year
    } else {
      return 'year'; // Show years for more than a year
    }
  };

  // Format date based on range type
  const formatDateByRange = (date, rangeType) => {
    if (!date) return '';
    const d = new Date(date);
    
    switch (rangeType) {
      case 'hour':
        return d.toLocaleString('en-IN', { 
          day: '2-digit', 
          month: 'short', 
          hour: '2-digit', 
          minute: '2-digit' 
        });
      case 'day':
        return d.toLocaleDateString('en-IN', { 
          day: '2-digit', 
          month: 'short' 
        });
      case 'month':
        return d.toLocaleDateString('en-IN', { 
          month: 'short', 
          year: 'numeric' 
        });
      case 'year':
        return d.toLocaleDateString('en-IN', { 
          year: 'numeric' 
        });
      default:
        return d.toLocaleDateString('en-IN', { 
          day: '2-digit', 
          month: 'short', 
          year: 'numeric' 
        });
    }
  };

  // Get column display name
  const getColumnDisplayName = (column) => {
    const names = {
      name: "Name",
      attemptedCallConsultation: "Attempted Call Consultation",
      attemptedChatConsultation: "Attempted Chat Consultation",
      missedCallConsultation: "Missed Call Consultation",
      missedChatConsultation: "Missed Chat Consultation",
      canceledCallConsultation: "Canceled Call Consultation",
      canceledChatConsultation: "Canceled Chat Consultation",
      freeChatConsultation: "Free Chat Consultation",
      freeCallConsultation: "Free Call Consultation",
      paidChatConsultation1: "Paid Chat Consultation",
      paidCallConsultation1: "Paid Call Consultation",
      paidChatConsultation2: "Paid Chat Consultation (Tier 2)",
      paidCallConsultation2: "Paid Call Consultation (Tier 2)",
      fixedSessions: "Fixed Sessions",
      revenueFromChat: "Revenue from Chat",
      revenueFromCall: "Revenue from Call",
      revenueFromFixedSession: "Revenue from Fixed Session",
      revenuePerConsultant: "Avg Revenue",
      giftPerConsultant: "Gift per Consultant",
      onlineTime: "Online Time",
      chatTime: "Chat Time",
      callTime: "Call Time",
      emergencyTime: "Emergency Time",
      ratingByUser: "Rating by User",
      followers: "Followers",
      profileView: "Profile View",
    };
    const text = names[column] || column;
    // Break text intelligently into 1-2 lines (not 1 word per line)
    const words = text.split(' ');
    if (words.length <= 2) return text;
    
    // For 3 words: first 2 on line 1, last on line 2
    if (words.length === 3) {
      return `${words[0]} ${words[1]}\n${words[2]}`;
    }
    
    // For 4 words: first 2 on line 1, last 2 on line 2
    if (words.length === 4) {
      return `${words[0]} ${words[1]}\n${words[2]} ${words[3]}`;
    }
    
    // For more words: split into 2 lines with roughly equal words
    const midPoint = Math.ceil(words.length / 2);
    const line1 = words.slice(0, midPoint).join(' ');
    const line2 = words.slice(midPoint).join(' ');
    return `${line1}\n${line2}`;
  };

  // Get column category
  const getColumnCategory = (column) => {
    if (column === "name") return "Basic Info";
    if (column.includes("Consultation") || column.includes("Session")) return "Consultation";
    if (column.includes("revenue") || column.includes("Revenue") || column.includes("gift")) return "Revenue";
    if (column.includes("Time")) return "Time";
    if (column === "ratingByUser" || column === "followers" || column === "profileView") return "Social";
    return "Other";
  };

  // Group columns by category
  const groupColumnsByCategory = () => {
    const groups = {};
    selectedColumns.forEach(column => {
      const category = getColumnCategory(column);
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(column);
    });
    return groups;
  };


  // Handle sort with direction
  const handleSort = (column, direction) => {
    setSortBy(column);
    setSortDirection(direction);
  };

  // Handle pagination
  const handlePageChange = (event, newPage) => {
    setPagination({ ...pagination, page: newPage + 1 });
  };

  const handleRowsPerPageChange = (event) => {
    setPagination({ ...pagination, limit: parseInt(event.target.value, 10), page: 1 });
  };

  // All available main metrics configurations
  const allMainMetricsConfig = [
    {
      key: "avgCancelledConsultationPercent",
      title: "Avg Cancelled Consultation %",
      value: formatNumber(mainMetrics.avgCancelledConsultationPercent) + "%",
      formula: "(Total Cancelled Consultations / Total Attempted Consultations) × 100",
    },
    {
      key: "avgMissedConsultationPercent",
      title: "Avg Missed Consultation %",
      value: formatNumber(mainMetrics.avgMissedConsultationPercent) + "%",
      formula: "(Total Missed Consultations / Total Attempted Consultations) × 100",
    },
    {
      key: "freeConsultationsPerConsultant",
      title: "Free Consultations per Consultant",
      value: formatNumber(mainMetrics.freeConsultationsPerConsultant),
      formula: "Total Free Consultations / Total Consultants",
    },
    {
      key: "paidConsultationsPerConsultantPercent",
      title: "Paid Consultations %",
      value: formatNumber(mainMetrics.paidConsultationsPerConsultantPercent) + "%",
      formula: "(Total Paid Consultations / (Total Free + Total Paid Consultations)) × 100",
    },
    {
      key: "revenuePerConsultant",
      title: "Avg Revenue",
      value: "₹" + formatNumber(mainMetrics.revenuePerConsultant),
      formula: "Total Revenue / Total Consultants",
    },
    {
      key: "avgOnlineTime",
      title: "Avg Online Time",
      value: formatTime(mainMetrics.avgOnlineTime),
      formula: "Total Online Time / Total Consultants",
    },
    {
      key: "avgBusyTime",
      title: "Avg Busy Time",
      value: formatTime(mainMetrics.avgBusyTime),
      formula: "Total Busy Time / Total Consultants",
    },
  ];

  // Custom metrics as cards (separate single boxes)
  const customMetricCards = Array.isArray(customMetrics) ? customMetrics.map((metric) => {
    return {
      key: metric.name,
      title: metric.name,
      value: formatNumber(mainMetrics[metric.name] || 0),
      formula: metric.formula || 'Custom formula',
      isCustom: true,
    };
  }) : [];

  // Grouped metrics
  const groupedMetrics = [];
  
  // Group 1: Online + Busy Time
  const onlineMetric = allMainMetricsConfig.find(m => m.key === 'avgOnlineTime');
  const busyMetric = allMainMetricsConfig.find(m => m.key === 'avgBusyTime');
  if (selectedMainMetrics.includes('avgOnlineTime') || selectedMainMetrics.includes('avgBusyTime')) {
    groupedMetrics.push({
      type: 'group',
      groupKey: 'timeMetrics',
      title: 'Time Metrics',
      metrics: [
        onlineMetric && selectedMainMetrics.includes('avgOnlineTime') ? {
          label: 'Online',
          value: onlineMetric.value,
          formula: onlineMetric.formula
        } : null,
        busyMetric && selectedMainMetrics.includes('avgBusyTime') ? {
          label: 'Busy',
          value: busyMetric.value,
          formula: busyMetric.formula
        } : null,
      ].filter(Boolean)
    });
  }

  // Group 2: Canceled + Missed
  const canceledMetric = allMainMetricsConfig.find(m => m.key === 'avgCancelledConsultationPercent');
  const missedMetric = allMainMetricsConfig.find(m => m.key === 'avgMissedConsultationPercent');
  if (selectedMainMetrics.includes('avgCancelledConsultationPercent') || selectedMainMetrics.includes('avgMissedConsultationPercent')) {
    groupedMetrics.push({
      type: 'group',
      groupKey: 'consultationStatus',
      title: 'Consultation Status',
      metrics: [
        canceledMetric && selectedMainMetrics.includes('avgCancelledConsultationPercent') ? {
          label: 'Canceled',
          value: canceledMetric.value,
          formula: canceledMetric.formula
        } : null,
        missedMetric && selectedMainMetrics.includes('avgMissedConsultationPercent') ? {
          label: 'Missed',
          value: missedMetric.value,
          formula: missedMetric.formula
        } : null,
      ].filter(Boolean)
    });
  }

  // Group 3: Paid + Free + Avg Revenue
  const paidMetric = allMainMetricsConfig.find(m => m.key === 'paidConsultationsPerConsultantPercent');
  const freeMetric = allMainMetricsConfig.find(m => m.key === 'freeConsultationsPerConsultant');
  const revenueMetric = allMainMetricsConfig.find(m => m.key === 'revenuePerConsultant');
  if (selectedMainMetrics.includes('paidConsultationsPerConsultantPercent') || 
      selectedMainMetrics.includes('freeConsultationsPerConsultant') || 
      selectedMainMetrics.includes('revenuePerConsultant')) {
    groupedMetrics.push({
      type: 'group',
      groupKey: 'revenueMetrics',
      title: 'Revenue & Consultations',
      metrics: [
        paidMetric && selectedMainMetrics.includes('paidConsultationsPerConsultantPercent') ? {
          label: 'Paid',
          value: paidMetric.value,
          formula: paidMetric.formula
        } : null,
        freeMetric && selectedMainMetrics.includes('freeConsultationsPerConsultant') ? {
          label: 'Free',
          value: freeMetric.value,
          formula: freeMetric.formula
        } : null,
        revenueMetric && selectedMainMetrics.includes('revenuePerConsultant') ? {
          label: 'Avg Revenue',
          value: revenueMetric.value,
          formula: revenueMetric.formula
        } : null,
      ].filter(Boolean)
    });
  }

  // Combine grouped metrics + custom metrics (as separate single boxes)
  const metricCards = [
    ...groupedMetrics,
    ...customMetricCards,
  ];

  return (
    <div className="WrapperArea">
      <div className="WrapperBox">
        {loader}
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Header */}
        <Box 
          sx={{ 
            mb: 3, 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2
          }}
        >
          <Box>
            <Typography 
              variant="h4" 
              component="h1"
              sx={{ 
                fontWeight: 700,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 0.5
              }}
            >
              Consultant Performance
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Comprehensive analytics and insights for all consultants
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Tooltip title="Refresh Data">
              <IconButton 
                onClick={() => fetchData()} 
                sx={{ 
                  bgcolor: "primary.main",
                  color: "white",
                  "&:hover": { bgcolor: "primary.dark" }
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={() => setColumnsDialogOpen(true)}
            >
              Columns
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setCustomMetricDialogOpen(true)}
            >
              Custom Metric
            </Button>
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={() => setMainMetricsDialogOpen(true)}
            >
              Customize Metrics
            </Button>
          </Box>
        </Box>

        {/* Date Filter Section with Search */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: "#f8f9fa", borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Search Consultant"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or email..."
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Date Filter</InputLabel>
                <Select
                  value={dateFilterType}
                  onChange={(e) => {
                    const filterType = e.target.value;
                    setDateFilterType(filterType);
                    const today = new Date();
                    today.setUTCHours(23, 59, 59, 999);
                    
                    switch (filterType) {
                      case "today":
                        setStartDate(new Date(today));
                        setEndDate(new Date(today));
                        break;
                      case "last_7_days":
                        const last7 = new Date(today);
                        last7.setDate(last7.getDate() - 7);
                        last7.setUTCHours(0, 0, 0, 0);
                        setStartDate(last7);
                        setEndDate(today);
                        break;
                      case "last_10_days":
                        const last10 = new Date(today);
                        last10.setDate(last10.getDate() - 10);
                        last10.setUTCHours(0, 0, 0, 0);
                        setStartDate(last10);
                        setEndDate(today);
                        break;
                      case "last_30_days":
                        const last30 = new Date(today);
                        last30.setDate(last30.getDate() - 30);
                        last30.setUTCHours(0, 0, 0, 0);
                        setStartDate(last30);
                        setEndDate(today);
                        break;
                      case "all_time":
                        // For all time, we'll use a very old date as start
                        // Backend will use each consultant's createdAt instead
                        const veryOldDate = new Date('2000-01-01');
                        veryOldDate.setUTCHours(0, 0, 0, 0);
                        setStartDate(veryOldDate);
                        setEndDate(today);
                        break;
                      case "custom":
                        // Keep current dates, user will select
                        break;
                      case "between":
                        // Keep current dates, user will select
                        break;
                      default:
                        break;
                    }
                  }}
                  label="Date Filter"
                >
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="last_7_days">Last 7 Days</MenuItem>
                  <MenuItem value="last_10_days">Last 10 Days</MenuItem>
                  <MenuItem value="last_30_days">Last 30 Days</MenuItem>
                  <MenuItem value="all_time">All Time</MenuItem>
                  <MenuItem value="custom">Custom Date</MenuItem>
                  <MenuItem value="between">Between Two Dates</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {dateFilterType === "custom" && (
              <Grid item xs={12} md={3}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    Select Date
                  </Typography>
                  <ReactDatePicker
                    selected={customSingleDate}
                    onChange={(date) => {
                      setCustomSingleDate(date);
                      const selectedDate = new Date(date);
                      selectedDate.setUTCHours(0, 0, 0, 0);
                      const endOfDay = new Date(date);
                      endOfDay.setUTCHours(23, 59, 59, 999);
                      setStartDate(selectedDate);
                      setEndDate(endOfDay);
                    }}
                    dateFormat="dd-MM-yyyy"
                    className="form-control"
                    placeholderText="Select Date"
                    style={{
                      width: "100%",
                      padding: "12px",
                      borderRadius: "8px",
                      border: "1px solid #ddd",
                    }}
                  />
                </Box>
              </Grid>
            )}
            
            {dateFilterType === "between" && (
              <>
                <Grid item xs={12} md={3}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                      Start Date
                    </Typography>
                    <ReactDatePicker
                      selected={startDate}
                      onChange={(date) => {
                        const selectedDate = new Date(date);
                        selectedDate.setUTCHours(0, 0, 0, 0);
                        setStartDate(selectedDate);
                      }}
                      maxDate={endDate}
                      dateFormat="dd-MM-yyyy"
                      className="form-control"
                      placeholderText="Select Start Date"
                      style={{
                        width: "100%",
                        padding: "12px",
                        borderRadius: "8px",
                        border: "1px solid #ddd",
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                      End Date
                    </Typography>
                    <ReactDatePicker
                      selected={endDate}
                      onChange={(date) => {
                        const selectedDate = new Date(date);
                        selectedDate.setUTCHours(23, 59, 59, 999);
                        setEndDate(selectedDate);
                      }}
                      minDate={startDate}
                      dateFormat="dd-MM-yyyy"
                      className="form-control"
                      placeholderText="Select End Date"
                      style={{
                        width: "100%",
                        padding: "12px",
                        borderRadius: "8px",
                        border: "1px solid #ddd",
                      }}
                    />
                  </Box>
                </Grid>
              </>
            )}
            
            {dateFilterType !== "custom" && dateFilterType !== "between" && (
              <Grid item xs={12} md={9}>
                <Typography variant="body2" color="textSecondary">
                  {dateFilterType === "today" 
                    ? `Showing data for: ${startDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`
                    : dateFilterType === "all_time"
                    ? `Showing data from registration date to today (All Time)`
                    : `Showing data from: ${startDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} to ${endDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`
                  }
                </Typography>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Main Metrics Cards */}
        <Grid container spacing={1.5} sx={{ mb: 3 }}>
          {metricCards.map((metric, index) => {
            // Grouped metric card
            if (metric.type === 'group') {
              return (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card
                    sx={{
                      height: "100%",
                      background: "white",
                      borderRadius: 2,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          color: "#757575",
                          mb: 1,
                        }}
                      >
                        {metric.title}
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
                        {metric.metrics.map((m, idx) => (
                          <Tooltip
                            key={idx}
                            title={
                              <Box sx={{ p: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                  Formula:
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                  {m.formula || 'N/A'}
                                </Typography>
                              </Box>
                            }
                            arrow
                            placement="top"
                          >
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "help" }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  color: "#212121",
                                }}
                              >
                                {m.label}
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontSize: "0.95rem",
                                  fontWeight: 500,
                                  color: "#212121",
                                }}
                              >
                                {m.value}
                              </Typography>
                            </Box>
                          </Tooltip>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            }
            
            // Single custom metric card
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    background: "white",
                    borderRadius: 2,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    },
                  }}
                >
                  <CardContent sx={{ p: 1.5 }}>
                    <Tooltip
                      title={
                        <Box sx={{ p: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Formula:
                          </Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {metric.formula || 'N/A'}
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, cursor: "help" }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            bgcolor: "#ff8989",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            flexShrink: 0,
                          }}
                        >
                          <TrendingUpIcon sx={{ fontSize: 20 }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.3 }}>
                            <Typography
                              color="textSecondary"
                              variant="body2"
                              sx={{
                                fontSize: "0.7rem",
                                fontWeight: 500,
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                color: "#212121",
                              }}
                            >
                              {metric.title}
                            </Typography>
                            {metric.isCustom && (
                              <Box sx={{ display: "flex", gap: 0.5 }}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const customIndex = customMetrics.findIndex(m => m.name === metric.key);
                                    if (customIndex !== -1) {
                                      handleEditCustomMetric(customIndex);
                                    }
                                  }}
                                  sx={{ 
                                    color: "#757575",
                                    p: 0.5,
                                    "&:hover": { bgcolor: "#f5f5f5" }
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const customIndex = customMetrics.findIndex(m => m.name === metric.key);
                                    if (customIndex !== -1) {
                                      handleRemoveCustomMetric(customIndex);
                                    }
                                  }}
                                  sx={{ 
                                    color: "#757575",
                                    p: 0.5,
                                    "&:hover": { bgcolor: "#f5f5f5" }
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            )}
                          </Box>
                          <Typography
                            variant="h6"
                            sx={{
                              fontSize: "1.05rem",
                              fontWeight: 500,
                              color: "#212121",
                            }}
                          >
                            {metric.value}
                          </Typography>
                        </Box>
                      </Box>
                    </Tooltip>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Data Table */}
        <Paper 
          sx={{ 
            borderRadius: 3,
            boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: 2, bgcolor: "#f8f9fa", borderBottom: "1px solid #e0e0e0" }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Consultant Performance Data
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {pagination.total} consultants found
            </Typography>
          </Box>
          <TableContainer sx={{ maxHeight: "70vh", overflowX: "auto", overflowY: "auto" }}>
            <Table stickyHeader sx={{ borderCollapse: "separate", borderSpacing: 0 }}>
              <TableHead>
                {/* Category Header Row */}
                <TableRow sx={{ bgcolor: "#e8f4f8", position: "sticky", top: 0, zIndex: 10 }}>
                  {(() => {
                    const columnGroups = groupColumnsByCategory();
                    const categoryOrder = ["Basic Info", "Consultation", "Revenue", "Time", "Social", "Other"];
                    const rows = [];
                    
                    categoryOrder.forEach(category => {
                      if (columnGroups[category] && columnGroups[category].length > 0) {
                        const isBasicInfo = category === "Basic Info";
                        rows.push(
                          <TableCell
                            key={category}
                            colSpan={columnGroups[category].length}
                            sx={{
                              bgcolor: "#e8f4f8",
                              border: "1px solid #c0c0c0",
                              borderRight: "1px solid #c0c0c0",
                              borderBottom: "1px solid #a0a0a0",
                              textAlign: "center",
                              py: 1,
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              color: "#1976d2",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              position: "sticky",
                              top: 0,
                              zIndex: 10,
                            }}
                          >
                            {category}
                          </TableCell>
                        );
                      }
                    });
                    
                    // Custom Metrics category
                    if (Array.isArray(customMetrics) && customMetrics.length > 0) {
                      rows.push(
                        <TableCell
                          key="Custom Metrics"
                          colSpan={customMetrics.length}
                          sx={{
                            bgcolor: "#fff3e0",
                            border: "1px solid #c0c0c0",
                            borderRight: "1px solid #c0c0c0",
                            borderBottom: "1px solid #a0a0a0",
                            textAlign: "center",
                            py: 1,
                            fontWeight: 700,
                            fontSize: "0.75rem",
                            color: "#f57c00",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            position: "sticky",
                            top: 0,
                            zIndex: 10,
                          }}
                        >
                          Custom Metrics
                        </TableCell>
                      );
                    }
                    
                    return rows;
                  })()}
                </TableRow>
                
                {/* Column Header Row */}
                <TableRow sx={{ bgcolor: "#f0f4f8", position: "sticky", top: "40px", zIndex: 9 }}>
                {selectedColumns.map((column) => (
                  <TableCell
                    key={column}
                    sx={{
                      userSelect: "none",
                      fontWeight: 700,
                      fontSize: "0.875rem",
                      color: "text.primary",
                      minWidth: "150px",
                      width: "auto",
                      whiteSpace: "normal",
                      bgcolor: sortBy === column ? "#e3f2fd" : "#f0f4f8",
                      border: "1px solid #c0c0c0",
                      borderRight: "1px solid #c0c0c0",
                      borderBottom: "2px solid #a0a0a0",
                      position: "sticky",
                      top: "40px",
                      zIndex: 9,
                    }}
                  >
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 700, 
                          whiteSpace: "pre-line",
                          textAlign: "center",
                          lineHeight: 1.3,
                          fontSize: "0.8rem",
                          minWidth: "120px"
                        }}
                      >
                        {getColumnDisplayName(column)}
                      </Typography>
                      {/* Improved Sort Button - Horizontal Layout */}
                      <Box 
                        sx={{ 
                          display: "flex", 
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "center",
                          border: sortBy === column ? "1.5px solid #1976d2" : "1px solid #d0d0d0",
                          borderRadius: "4px",
                          overflow: "hidden",
                          minWidth: "40px",
                          minHeight: "20px",
                          bgcolor: sortBy === column ? "#e3f2fd" : "#ffffff",
                          boxShadow: sortBy === column ? "0 1px 3px rgba(25, 118, 210, 0.2)" : "0 1px 2px rgba(0,0,0,0.08)",
                          "&:hover": {
                            borderColor: "#1976d2",
                            bgcolor: "#e3f2fd",
                            boxShadow: "0 2px 4px rgba(25, 118, 210, 0.25)",
                            transform: "translateY(-1px)",
                          },
                          transition: "all 0.2s ease",
                        }}
                      >
                        {/* Ascending Button (Left) */}
                        <Tooltip title={`Sort ${getColumnDisplayName(column)} in ascending order`} arrow>
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSort(column, "asc");
                            }}
                            sx={{
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "20px",
                              height: "20px",
                              bgcolor: sortBy === column && sortDirection === "asc" ? "#1976d2" : "transparent",
                              color: sortBy === column && sortDirection === "asc" ? "white" : "#757575",
                              borderRight: "0.5px solid #e0e0e0",
                              "&:hover": {
                                bgcolor: sortBy === column && sortDirection === "asc" ? "#1565c0" : "#e3f2fd",
                                color: sortBy === column && sortDirection === "asc" ? "white" : "#1976d2",
                              },
                              transition: "all 0.2s ease",
                            }}
                          >
                            <ArrowBackIcon sx={{ fontSize: "0.7rem", fontWeight: "bold" }} />
                          </Box>
                        </Tooltip>
                        {/* Descending Button (Right) */}
                        <Tooltip title={`Sort ${getColumnDisplayName(column)} in descending order`} arrow>
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSort(column, "desc");
                            }}
                            sx={{
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "20px",
                              height: "20px",
                              bgcolor: sortBy === column && sortDirection === "desc" ? "#1976d2" : "transparent",
                              color: sortBy === column && sortDirection === "desc" ? "white" : "#757575",
                              "&:hover": {
                                bgcolor: sortBy === column && sortDirection === "desc" ? "#1565c0" : "#e3f2fd",
                                color: sortBy === column && sortDirection === "desc" ? "white" : "#1976d2",
                              },
                              transition: "all 0.2s ease",
                            }}
                          >
                            <ArrowForwardIcon sx={{ fontSize: "0.7rem", fontWeight: "bold" }} />
                          </Box>
                        </Tooltip>
                      </Box>
                    </Box>
                  </TableCell>
                ))}
                {Array.isArray(customMetrics) && customMetrics.map((metric) => (
                  <TableCell
                    key={metric.name}
                    sx={{
                      userSelect: "none",
                      fontWeight: "bold",
                      minWidth: "140px",
                      width: "auto",
                      bgcolor: sortBy === metric.name ? "#ffe0b2" : "#fff3e0",
                      border: "1px solid #c0c0c0",
                      borderRight: "1px solid #c0c0c0",
                      borderBottom: "2px solid #a0a0a0",
                    }}
                  >
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 700, 
                          whiteSpace: "pre-line",
                          textAlign: "center",
                          lineHeight: 1.3,
                          fontSize: "0.8rem"
                        }}
                      >
                        {(() => {
                          const words = metric.name.split(' ');
                          if (words.length <= 2) return metric.name;
                          if (words.length === 3) return `${words[0]} ${words[1]}\n${words[2]}`;
                          if (words.length === 4) return `${words[0]} ${words[1]}\n${words[2]} ${words[3]}`;
                          const midPoint = Math.ceil(words.length / 2);
                          return `${words.slice(0, midPoint).join(' ')}\n${words.slice(midPoint).join(' ')}`;
                        })()}
                      </Typography>
                      {/* Improved Sort Button - Horizontal Layout */}
                      <Box 
                        sx={{ 
                          display: "flex", 
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "center",
                          border: sortBy === metric.name ? "1.5px solid #f57c00" : "1px solid #d0d0d0",
                          borderRadius: "4px",
                          overflow: "hidden",
                          minWidth: "40px",
                          minHeight: "20px",
                          bgcolor: sortBy === metric.name ? "#ffe0b2" : "#ffffff",
                          boxShadow: sortBy === metric.name ? "0 1px 3px rgba(245, 124, 0, 0.2)" : "0 1px 2px rgba(0,0,0,0.08)",
                          "&:hover": {
                            borderColor: "#f57c00",
                            bgcolor: "#ffe0b2",
                            boxShadow: "0 2px 4px rgba(245, 124, 0, 0.25)",
                            transform: "translateY(-1px)",
                          },
                          transition: "all 0.2s ease",
                        }}
                      >
                        {/* Ascending Button (Left) */}
                        <Tooltip title={`Sort ${metric.name} in ascending order`} arrow>
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSort(metric.name, "asc");
                            }}
                            sx={{
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "20px",
                              height: "20px",
                              bgcolor: sortBy === metric.name && sortDirection === "asc" ? "#f57c00" : "transparent",
                              color: sortBy === metric.name && sortDirection === "asc" ? "white" : "#757575",
                              borderRight: "0.5px solid #e0e0e0",
                              "&:hover": {
                                bgcolor: sortBy === metric.name && sortDirection === "asc" ? "#e65100" : "#ffe0b2",
                                color: sortBy === metric.name && sortDirection === "asc" ? "white" : "#f57c00",
                              },
                              transition: "all 0.2s ease",
                            }}
                          >
                            <ArrowBackIcon sx={{ fontSize: "0.7rem", fontWeight: "bold" }} />
                          </Box>
                        </Tooltip>
                        {/* Descending Button (Right) */}
                        <Tooltip title={`Sort ${metric.name} in descending order`} arrow>
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSort(metric.name, "desc");
                            }}
                            sx={{
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "20px",
                              height: "20px",
                              bgcolor: sortBy === metric.name && sortDirection === "desc" ? "#f57c00" : "transparent",
                              color: sortBy === metric.name && sortDirection === "desc" ? "white" : "#757575",
                              "&:hover": {
                                bgcolor: sortBy === metric.name && sortDirection === "desc" ? "#e65100" : "#ffe0b2",
                                color: sortBy === metric.name && sortDirection === "desc" ? "white" : "#f57c00",
                              },
                              transition: "all 0.2s ease",
                            }}
                          >
                            <ArrowForwardIcon sx={{ fontSize: "0.7rem", fontWeight: "bold" }} />
                          </Box>
                        </Tooltip>
                      </Box>
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {consultants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={selectedColumns.length + (Array.isArray(customMetrics) ? customMetrics.length : 0)} align="center" sx={{ py: 5, border: "1px solid #e0e0e0" }}>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="h6" color="textSecondary" sx={{ mb: 1 }}>
                        No Data Available
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Try adjusting your filters or date range
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                consultants.map((consultant, index) => (
                  <TableRow 
                    key={consultant.consultantId}
                    sx={{
                      "&:nth-of-type(even)": { bgcolor: "#fafafa" },
                      "&:hover": { bgcolor: "#f5f5f5" },
                      transition: "background-color 0.2s",
                    }}
                  >
                    {selectedColumns.map((column) => (
                      <TableCell 
                        key={column}
                        align="center"
                        sx={{
                          border: "1px solid #e0e0e0",
                          borderRight: "1px solid #e0e0e0",
                          borderTop: "none",
                          borderBottom: "1px solid #e0e0e0",
                          py: 1.5,
                          position: "relative",
                        }}
                      >
                        {column === "name" ? (
                          <Typography sx={{ fontWeight: 600, color: "primary.main", textAlign: "center" }}>
                            {consultant[column]}
                          </Typography>
                        ) : column.includes("Time") ? (
                          <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <Chip 
                              label={formatTime(consultant[column])} 
                              size="small" 
                              color="info"
                              variant="outlined"
                            />
                          </Box>
                        ) : column.includes("revenue") || column.includes("Revenue") || column.includes("gift") ? (
                          <Typography sx={{ fontWeight: 600, color: "#4caf50", textAlign: "center" }}>
                            ₹{formatNumber(consultant[column])}
                          </Typography>
                        ) : (
                          <Typography sx={{ textAlign: "center" }}>
                            {formatNumber(consultant[column])}
                          </Typography>
                        )}
                      </TableCell>
                    ))}
                    {Array.isArray(customMetrics) && customMetrics.map((metric) => (
                      <TableCell 
                        key={metric.name}
                        align="center"
                        sx={{
                          border: "1px solid #e0e0e0",
                          borderRight: "1px solid #e0e0e0",
                          borderTop: "none",
                          borderBottom: "1px solid #e0e0e0",
                          py: 1.5,
                        }}
                      >
                        <Typography sx={{ fontWeight: 500, textAlign: "center" }}>
                          {formatNumber(consultant[metric.name] || 0)}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={pagination.total}
            page={pagination.page - 1}
            onPageChange={handlePageChange}
            rowsPerPage={pagination.limit}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{ borderTop: "1px solid #e0e0e0" }}
          />
        </TableContainer>
        </Paper>

   {/* Graph Display Section - Below Static Data */}
   <Paper sx={{ p: 5, mb: 3, bgcolor: "#fafafa", borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.1)",marginTop: "40px" }}>
          <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 700, color: "#1976d2" }}>
                Performance Visualization
              </Typography>
              {/* Date Filter for Graph */}
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel>Date Filter</InputLabel>
                <Select
                  value={graphDateFilterType}
                  onChange={(e) => {
                    const filterType = e.target.value;
                    setGraphDateFilterType(filterType);
                    const today = new Date();
                    today.setUTCHours(23, 59, 59, 999);
                    
                    switch (filterType) {
                      case "today":
                        setGraphStartDate(new Date(today));
                        setGraphEndDate(new Date(today));
                        break;
                      case "last_7_days":
                        const last7 = new Date(today);
                        last7.setDate(last7.getDate() - 7);
                        last7.setUTCHours(0, 0, 0, 0);
                        setGraphStartDate(last7);
                        setGraphEndDate(today);
                        break;
                      case "last_10_days":
                        const last10 = new Date(today);
                        last10.setDate(last10.getDate() - 10);
                        last10.setUTCHours(0, 0, 0, 0);
                        setGraphStartDate(last10);
                        setGraphEndDate(today);
                        break;
                      case "last_30_days":
                        const last30 = new Date(today);
                        last30.setDate(last30.getDate() - 30);
                        last30.setUTCHours(0, 0, 0, 0);
                        setGraphStartDate(last30);
                        setGraphEndDate(today);
                        break;
                      case "all_time":
                        const veryOldDate = new Date('2000-01-01');
                        veryOldDate.setUTCHours(0, 0, 0, 0);
                        setGraphStartDate(veryOldDate);
                        setGraphEndDate(today);
                        break;
                      case "custom":
                        break;
                      case "between":
                        break;
                      default:
                        break;
                    }
                  }}
                  label="Date Filter"
                >
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="last_7_days">Last 7 Days</MenuItem>
                  <MenuItem value="last_10_days">Last 10 Days</MenuItem>
                  <MenuItem value="last_30_days">Last 30 Days</MenuItem>
                  <MenuItem value="all_time">All Time</MenuItem>
                  <MenuItem value="custom">Custom Date</MenuItem>
                  <MenuItem value="between">Between Two Dates</MenuItem>
                </Select>
              </FormControl>
              
              {/* Custom Date Picker (shown when custom is selected) */}
              {graphDateFilterType === "custom" && (
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    Select Date
                  </Typography>
                  <ReactDatePicker
                    selected={graphCustomSingleDate}
                    onChange={(date) => {
                      const selectedDate = new Date(date);
                      selectedDate.setUTCHours(0, 0, 0, 0);
                      setGraphStartDate(selectedDate);
                      setGraphEndDate(selectedDate);
                      setGraphCustomSingleDate(selectedDate);
                    }}
                    dateFormat="dd-MM-yyyy"
                    className="form-control"
                    placeholderText="Select Date"
                    style={{
                      width: "100%",
                      padding: "12px",
                      borderRadius: "8px",
                      border: "1px solid #ddd",
                    }}
                  />
                </Box>
              )}
              
              {/* Between Two Dates (shown when between is selected) */}
              {graphDateFilterType === "between" && (
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                      Start Date
                    </Typography>
                    <ReactDatePicker
                      selected={graphStartDate}
                      onChange={(date) => {
                        const selectedDate = new Date(date);
                        selectedDate.setUTCHours(0, 0, 0, 0);
                        setGraphStartDate(selectedDate);
                      }}
                      dateFormat="dd-MM-yyyy"
                      className="form-control"
                      placeholderText="Select Start Date"
                      style={{
                        width: "100%",
                        padding: "12px",
                        borderRadius: "8px",
                        border: "1px solid #ddd",
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                      End Date
                    </Typography>
                    <ReactDatePicker
                      selected={graphEndDate}
                      onChange={(date) => {
                        const selectedDate = new Date(date);
                        selectedDate.setUTCHours(23, 59, 59, 999);
                        setGraphEndDate(selectedDate);
                      }}
                      minDate={graphStartDate}
                      dateFormat="dd-MM-yyyy"
                      className="form-control"
                      placeholderText="Select End Date"
                      style={{
                        width: "100%",
                        padding: "12px",
                        borderRadius: "8px",
                        border: "1px solid #ddd",
                      }}
                    />
                  </Box>
                </Box>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {/* Graph Mode Selection */}
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Graph Mode</InputLabel>
                <Select
                  value={graphMode}
                  onChange={(e) => {
                    setGraphMode(e.target.value);
                    if (e.target.value === "group") {
                      setSelectedConsultantsForGraph([]);
                    } else {
                      setSelectedGroupsForComparison([]);
                    }
                  }}
                  label="Graph Mode"
                >
                  <MenuItem value="individual">Individual Consultants</MenuItem>
                  <MenuItem value="group">Group Comparison</MenuItem>
                </Select>
              </FormControl>

              {/* Individual Consultant Selection (shown when mode is individual) */}
              {graphMode === "individual" && (
                <FormControl sx={{ minWidth: 280 }}>
                  <InputLabel>Select Consultants (Optional)</InputLabel>
                <Select
                  multiple
                  value={selectedConsultantsForGraph}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedConsultantsForGraph(typeof value === 'string' ? value.split(',') : value);
                  }}
                  label="Select Consultants (Optional)"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: "60px", overflowY: "auto" }}>
                      {selected.length === 0 ? (
                        <Typography variant="body2" color="textSecondary">All Consultants</Typography>
                      ) : (
                        selected
                          .filter(v => v != null)
                          .map((value) => {
                            const consultantName = typeof value === 'string' ? value : value?.name || value || '';
                            if (!consultantName) return null;
                            return (
                              <Chip 
                                key={consultantName} 
                                label={String(consultantName).substring(0, 20)} 
                                size="small"
                                sx={{ height: "24px", fontSize: "0.75rem" }}
                                onDelete={() => {
                                  setSelectedConsultantsForGraph(selected.filter(v => {
                                    const vName = typeof v === 'string' ? v : v?.name || v || '';
                                    return vName !== consultantName;
                                  }));
                                }}
                              />
                            );
                          })
                          .filter(item => item != null)
                      )}
                    </Box>
                  )}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 400,
                      },
                    },
                  }}
                >
                  {/* Search Box */}
                  <Box sx={{ p: 1, borderBottom: "1px solid #e0e0e0" }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search consultants..."
                      value={consultantSearchText}
                      onChange={(e) => setConsultantSearchText(e.target.value)}
                      InputProps={{
                        startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
                      }}
                      sx={{ mb: 1 }}
                    />
                    <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setSelectedConsultantsForGraph([]);
                          setConsultantSearchText("");
                        }}
                        sx={{ fontSize: "0.75rem" }}
                      >
                        Clear All
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          const consultantList = allConsultants.length > 0 ? allConsultants : consultants;
                          const allConsultantNames = consultantList
                            .filter(c => c != null) // Filter out null/undefined
                            .map(c => c?.name || c?.consultantName || String(c || ''));
                          setSelectedConsultantsForGraph(allConsultantNames);
                        }}
                        sx={{ fontSize: "0.75rem" }}
                      >
                        Select All
                      </Button>
                    </Box>
                  </Box>
                  
                  {/* Consultant List - Show all consultants */}
                  {(allConsultants.length > 0 ? allConsultants : consultants)
                    .filter(consultant => consultant != null) // Filter out null/undefined first
                    .filter(consultant => {
                      if (!consultantSearchText) return true;
                      const name = consultant?.name || consultant?.consultantName || String(consultant || '');
                      return name.toLowerCase().includes(consultantSearchText.toLowerCase());
                    })
                    .map((consultant) => {
                      if (!consultant) return null; // Safety check
                      const consultantName = consultant?.name || consultant?.consultantName || String(consultant || '');
                      const isSelected = selectedConsultantsForGraph.some(selected => {
                        const selectedName = typeof selected === 'string' ? selected : selected?.name || selected;
                        return String(selectedName) === String(consultantName);
                      });
                      return (
                        <MenuItem 
                          key={consultant?.consultantId || consultantName} 
                          value={consultantName}
                        >
                          <Checkbox checked={isSelected} />
                          <ListItemText primary={consultantName} />
                        </MenuItem>
                      );
                    })
                    .filter(item => item != null) // Remove any null items
                  }
                </Select>
                <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, fontSize: "0.7rem" }}>
                  {allConsultants.length > 0 
                    ? `${allConsultants.length} consultants available • Leave empty to show all`
                    : consultants.length > 0
                    ? `${consultants.length} consultants available • Leave empty to show all`
                    : "Leave empty to show all consultants"}
                </Typography>
              </FormControl>
              )}

              {/* Group Management (shown when mode is group) */}
              {graphMode === "group" && (
                <>
                  {groups.map((group) => (
                    <FormControl key={group.id} sx={{ minWidth: 250 }}>
                      <InputLabel>{group.name}</InputLabel>
                      <Select
                        multiple
                        value={group.consultants}
                        onChange={(e) => {
                          const value = e.target.value;
                          const newConsultants = typeof value === 'string' ? value.split(',') : value;
                          // Filter out undefined, null, or empty values
                          const filteredConsultants = newConsultants.filter(v => {
                            const consultantName = typeof v === 'string' ? v : v?.name || v;
                            return consultantName && consultantName !== 'undefined' && consultantName !== 'null' && consultantName.trim() !== '';
                          });
                          setGroups(groups.map(g => 
                            g.id === group.id 
                              ? { ...g, consultants: filteredConsultants }
                              : g
                          ));
                        }}
                        label={group.name}
                        renderValue={(selected) => {
                          // Filter out undefined, null, or empty values
                          const validSelected = selected.filter(v => {
                            const consultantName = typeof v === 'string' ? v : v?.name || v;
                            return consultantName && consultantName !== 'undefined' && consultantName !== 'null' && consultantName.trim() !== '';
                          });
                          
                          return (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: "60px", overflowY: "auto" }}>
                              {validSelected.length === 0 ? (
                                <Typography variant="body2" color="textSecondary">No consultants</Typography>
                              ) : (
                                validSelected.slice(0, 3).map((value) => {
                                  const consultantName = typeof value === 'string' ? value : value?.name || value;
                                  if (!consultantName || consultantName === 'undefined' || consultantName === 'null') return null;
                                  return (
                                    <Chip 
                                      key={consultantName} 
                                      label={String(consultantName).substring(0, 15)} 
                                      size="small"
                                      sx={{ height: "22px", fontSize: "0.7rem" }}
                                    />
                                  );
                                }).filter(item => item != null)
                              )}
                              {validSelected.length > 3 && (
                                <Chip
                                  label={`+${validSelected.length - 3}`}
                                  size="small"
                                  sx={{ height: "22px", fontSize: "0.7rem" }}
                                />
                              )}
                            </Box>
                          );
                        }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 400,
                            },
                          },
                        }}
                      >
                        <Box sx={{ p: 1, borderBottom: "1px solid #e0e0e0" }}>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Search consultants..."
                            value={consultantSearchText}
                            onChange={(e) => setConsultantSearchText(e.target.value)}
                            InputProps={{
                              startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
                            }}
                            sx={{ mb: 1 }}
                          />
                        </Box>
                        {(allConsultants.length > 0 ? allConsultants : consultants)
                          .filter(consultant => consultant != null)
                          .filter(consultant => {
                            if (!consultantSearchText) return true;
                            const name = consultant?.name || consultant?.consultantName || String(consultant || '');
                            return name.toLowerCase().includes(consultantSearchText.toLowerCase());
                          })
                          .map((consultant) => {
                            if (!consultant) return null;
                            const consultantName = consultant?.name || consultant?.consultantName || String(consultant || '');
                            const isSelected = group.consultants.some(selected => {
                              const selectedName = typeof selected === 'string' ? selected : selected?.name || selected;
                              return String(selectedName) === String(consultantName);
                            });
                            return (
                              <MenuItem 
                                key={consultant?.consultantId || consultantName} 
                                value={consultantName}
                              >
                                <Checkbox checked={isSelected} />
                                <ListItemText primary={consultantName} />
                              </MenuItem>
                            );
                          })
                          .filter(item => item != null)
                        }
                      </Select>
                    </FormControl>
                  ))}
                  
                  {/* Group Selection for Comparison */}
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Select Groups to Compare</InputLabel>
                    <Select
                      multiple
                      value={selectedGroupsForComparison}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSelectedGroupsForComparison(typeof value === 'string' ? value.split(',') : value);
                      }}
                      label="Select Groups to Compare"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((groupId) => {
                            const group = groups.find(g => g.id === groupId);
                            return (
                              <Chip 
                                key={groupId} 
                                label={group?.name || `Group ${groupId}`} 
                                size="small"
                                sx={{ height: "24px" }}
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {groups.map((group) => (
                        <MenuItem key={group.id} value={group.id}>
                          <Checkbox checked={selectedGroupsForComparison.includes(group.id)} />
                          <ListItemText 
                            primary={group.name}
                            secondary={`${group.consultants.length} consultant(s)`}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}

              {/* Field Selection Dropdown - Multiple */}
              <FormControl sx={{ minWidth: 250 }}>
                <InputLabel>Select Fields (Multiple)</InputLabel>
                <Select
                  multiple
                  value={selectedFields}
                  onChange={(e) => {
                    const value = e.target.value;
                    const newFields = typeof value === 'string' ? value.split(',') : value;
                    // Limit to maximum 4 fields
                    if (newFields.length <= 4) {
                      setSelectedFields(newFields);
                    } else {
                      toastMessage("Maximum 4 fields can be selected", "warning");
                    }
                  }}
                  label="Select Fields (Multiple)"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip 
                          key={value} 
                          label={getColumnDisplayName(value)} 
                          size="small"
                          sx={{ height: "24px" }}
                        />
                      ))}
                    </Box>
                  )}
                >
                  {allColumns
                    .filter(col => ![
                      'name', 'email', 'phone', 'phonePrefix',
                      'gender', 'profileImage', 'status', 'goLive', 'price', 'highPriority',
                      'highestQualification', 'primarySkills', 'otherSkills', 'expertise',
                      'greenTick', 'bio', 'experienceYear', 'dailyWorkHour', 'language',
                      'city', 'state', 'pincode', 'address'
                    ].includes(col))
                    .map((col) => (
                      <MenuItem key={col} value={col}>
                        <Checkbox checked={selectedFields.indexOf(col) > -1} />
                        <ListItemText primary={getColumnDisplayName(col)} />
                      </MenuItem>
                    ))}
                  {Array.isArray(customMetrics) && customMetrics.map((metric) => (
                    <MenuItem key={metric.name} value={metric.name}>
                      <Checkbox checked={selectedFields.indexOf(metric.name) > -1} />
                      <ListItemText primary={metric.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                startIcon={<BarChartIcon />}
                onClick={handleFetchGraph}
                disabled={
                  graphLoading || 
                  selectedFields.length === 0 || 
                  (graphMode === "group" && selectedGroupsForComparison.length === 0)
                }
                sx={{ 
                  boxShadow: 1,
                  "&:hover": { boxShadow: 2 }
                }}
              >
                {graphLoading ? "Generating..." : "Generate"}
              </Button>

              {/* <Button
                variant="outlined"
                startIcon={<BarChartIcon />}
                onClick={() => setGraphDialogOpen(true)}
                disabled={!graphData || !graphData.series || graphData.series.length === 0}
                sx={{ 
                  boxShadow: 1,
                  "&:hover": { boxShadow: 2 }
                }}
              >
                View Graph
              </Button> */}
            </Box>
          </Box>
          
          {chartData && chartData.series && Array.isArray(chartData.series) && chartData.series.length > 0 ? (
            <>
              {/* Graph Title and Subtitle */}
              <Box sx={{ mb: 3, textAlign: "center" }}>
                {/* <Typography 
                  variant="h4" 
                  sx={{ 
                    mb: 1, 
                    fontWeight: 700, 
                    color: "#212121",
                    fontSize: { xs: "1.5rem", md: "2rem" }
                  }}
                >
                  {selectedFields.length > 0 
                    ? `${selectedFields.map(f => getColumnDisplayName(f)).join(" & ")} Analytics`
                    : "Performance Analytics"}
                </Typography> */}
                {/* <Typography 
                  variant="body1" 
                  sx={{ 
                    color: "#757575",
                    fontSize: "1rem",
                    fontWeight: 500
                  }}
                >
                  Track your performance metrics over time
                </Typography> */}
              </Box>
              
              {/* Date Range Info */}
              <Box sx={{ mb: 3, p: 2.5, bgcolor: "#e3f2fd", borderRadius: 2, border: "2px solid #90caf9" }}>
                <Typography variant="body1" sx={{ fontWeight: 700, color: "#1976d2", mb: 1, fontSize: "1rem" }}>
                  📅 Data Range:
                </Typography>
                <Typography variant="body2" sx={{ color: "#424242", fontSize: "0.95rem", fontWeight: 500 }}>
                  {dateFilterType === "all_time" ? (
                    <>
                      <strong>From:</strong> January 2025 
                      {" "} <strong>To:</strong> {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      <Box component="span" sx={{ ml: 1, fontSize: "0.85rem", color: "#757575" }}>
                        (Month-wise)
                      </Box>
                    </>
                  ) : (
                    <>
                      <strong>From:</strong> {formatDateByRange(startDate, getDateRangeFormat(startDate, endDate))} 
                      {" "} <strong>To:</strong> {formatDateByRange(endDate, getDateRangeFormat(startDate, endDate))}
                      <Box component="span" sx={{ ml: 1, fontSize: "0.85rem", color: "#757575" }}>
                        ({getDateRangeFormat(startDate, endDate) === 'hour' ? 'Hour-wise' : 
                          getDateRangeFormat(startDate, endDate) === 'day' ? 'Day-wise' :
                          getDateRangeFormat(startDate, endDate) === 'month' ? 'Month-wise' : 'Year-wise'})
                      </Box>
                    </>
                  )}
                  {selectedFields.length > 0 && (
                    <>
                      <br />
                      <Box component="span" sx={{ mt: 1, display: "inline-block" }}>
                        Metrics: <strong>{selectedFields.map(f => getColumnDisplayName(f)).join(", ")}</strong>
                      </Box>
                    </>
                  )}
                  {selectedConsultantsForGraph.length > 0 ? (
                    <>
                      <br />
                      <Box component="span" sx={{ mt: 1, display: "inline-block" }}>
                        <strong>Selected Consultants ({selectedConsultantsForGraph.length}):</strong>
                        <Box component="span" sx={{ ml: 1, fontSize: "0.9rem" }}>
                          {selectedConsultantsForGraph
                            .filter(c => c != null)
                            .map((c, idx) => {
                              const consultantName = typeof c === 'string' ? c : c?.name || c || '';
                              return consultantName;
                            })
                            .filter(name => name)
                            .slice(0, 5)
                            .map((name, idx) => (
                              <Chip
                                key={idx}
                                label={name}
                                size="small"
                                sx={{ 
                                  height: "22px", 
                                  fontSize: "0.75rem",
                                  mr: 0.5,
                                  mb: 0.5,
                                  bgcolor: "#e3f2fd",
                                  color: "#1976d2",
                                  fontWeight: 600
                                }}
                              />
                            ))}
                          {selectedConsultantsForGraph.filter(c => c != null).length > 5 && (
                            <Chip
                              label={`+${selectedConsultantsForGraph.filter(c => c != null).length - 5} more`}
                              size="small"
                              sx={{ 
                                height: "22px", 
                                fontSize: "0.75rem",
                                mr: 0.5,
                                mb: 0.5,
                                bgcolor: "#f5f5f5",
                                color: "#757575"
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    </>
                  ) : (
                    <>
                      <br />
                      <Box component="span" sx={{ mt: 1, display: "inline-block", fontSize: "0.9rem", color: "#757575" }}>
                        Showing data for: <strong>All Consultants</strong>
                      </Box>
                    </>
                  )}
                </Typography>
              </Box>
              
              <Box sx={{ 
                bgcolor: "white", 
                borderRadius: 3, 
                p: { xs: 2, sm: 4 }, 
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                overflow: "hidden"
              }}>
                {graphLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '350px' }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box sx={{ 
                    overflowX: "auto",
                    overflowY: "hidden",
                    width: "100%",
                    "&::-webkit-scrollbar": {
                      height: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "#f1f1f1",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#888",
                      borderRadius: "4px",
                      "&:hover": {
                        background: "#555",
                      },
                    },
                  }}>
                    <Box sx={{ minWidth: { xs: "600px", sm: "100%" } }}>
                      <ReactApexChart
                        options={chartData?.options}
                        series={chartData?.series}
                        type={graphType === "bar" ? "bar" : graphType === "line" ? "line" : "area"}
                        height={350}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            </>
          ) : (
            <Box sx={{ 
              bgcolor: "white", 
              borderRadius: 3, 
              p: 5, 
              textAlign: "center",
              border: "2px dashed #e0e0e0"
            }}>
              <BarChartIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
              <Typography variant="h6" color="textSecondary" sx={{ mb: 1 }}>
                No Graph Data
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Select field(s) and click "Generate" to visualize data
              </Typography>
            </Box>
          )}
        </Paper>
        {/* Columns Selection Dialog */}
        <Dialog
          open={columnsDialogOpen}
          onClose={() => setColumnsDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Select Columns</DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2 }}>
              {allColumns.map((column) => (
                <FormControlLabel
                  key={column}
                  control={
                    <Checkbox
                      checked={selectedColumns.includes(column)}
                      onChange={() => handleColumnToggle(column)}
                    />
                  }
                  label={getColumnDisplayName(column)}
                />
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setColumnsDialogOpen(false)}>Close</Button>
            <Button
              variant="contained"
              onClick={() => {
                setColumnsDialogOpen(false);
                fetchData();
              }}
            >
              Apply
            </Button>
          </DialogActions>
        </Dialog>

        {/* Main Metrics Customization Dialog */}
        <Dialog
          open={mainMetricsDialogOpen}
          onClose={() => setMainMetricsDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Customize Main Metrics</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Select which metrics to display in the main metrics cards section
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2 }}>
              {allMainMetricsConfig.map((metric) => (
                <FormControlLabel
                  key={metric.key}
                  control={
                    <Checkbox
                      checked={selectedMainMetrics.includes(metric.key)}
                      onChange={() => handleMainMetricToggle(metric.key)}
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ color: metric.color }}>{metric.icon}</Box>
                      <Typography>{metric.title}</Typography>
                    </Box>
                  }
                />
              ))}
            </Box>
            {Array.isArray(customMetrics) && customMetrics.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Custom Metrics (Auto-added)
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {customMetrics.map((metric) => (
                    <Chip
                      key={metric.name}
                      label={metric.name}
                      color="primary"
                      variant="outlined"
                      sx={{ width: "fit-content" }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMainMetricsDialogOpen(false)}>Close</Button>
            <Button
              variant="contained"
              onClick={() => {
                setMainMetricsDialogOpen(false);
              }}
            >
              Apply
            </Button>
          </DialogActions>
        </Dialog>

        {/* Custom Metric Dialog */}
        <Dialog
          open={customMetricDialogOpen}
          onClose={() => {
            setCustomMetricDialogOpen(false);
            setNewMetricName("");
            setNewMetricFormula("");
            setEditingMetricIndex(null);
            setSelectedFieldForFormula("");
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editingMetricIndex !== null ? "Edit Custom Metric" : "Create Custom Metric"}
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Metric Name"
              value={newMetricName}
              onChange={(e) => setNewMetricName(e.target.value)}
              sx={{ mt: 2, mb: 2 }}
              placeholder="e.g., Revenue Efficiency"
            />
            
            {/* Field Selector */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Available Fields (Click to add to formula):
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                {allColumns.map((column) => (
                  <Chip
                    key={column}
                    label={getColumnDisplayName(column)}
                    onClick={() => {
                      const fieldName = column;
                      setNewMetricFormula(prev => {
                        const current = prev || "";
                        return current ? `${current} ${fieldName}` : fieldName;
                      });
                    }}
                    sx={{ cursor: "pointer" }}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>

            <TextField
              fullWidth
              label="Formula"
              value={newMetricFormula}
              onChange={(e) => setNewMetricFormula(e.target.value)}
              multiline
              rows={4}
              placeholder="e.g., (revenuePerConsultant / paidCallConsultation1) * 100"
              helperText="Use metric names from the table. Supported operators: +, -, *, /, <, >, ="
            />
            
            <Box sx={{ mt: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Quick Operators:
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {["+", "-", "*", "/", "(", ")", "<", ">", "="].map((op) => (
                  <Button
                    key={op}
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setNewMetricFormula(prev => {
                        const current = prev || "";
                        return current ? `${current} ${op}` : op;
                      });
                    }}
                    sx={{ minWidth: "40px" }}
                  >
                    {op}
                  </Button>
                ))}
              </Box>
            </Box>

            <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: "block" }}>
              <strong>Available metrics:</strong> revenuePerConsultant, paidCallConsultation1, paidChatConsultation1, 
              attemptedCallConsultation, attemptedChatConsultation, revenueFromChat, revenueFromCall, etc.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                setCustomMetricDialogOpen(false);
                setNewMetricName("");
                setNewMetricFormula("");
                setEditingMetricIndex(null);
                setSelectedFieldForFormula("");
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleAddCustomMetric}
              disabled={!newMetricName || !newMetricFormula}
            >
              {editingMetricIndex !== null ? "Update Metric" : "Add Metric"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Graph Display Dialog */}
        <Dialog
          open={graphDialogOpen}
          onClose={() => {
            setGraphDialogOpen(false);
          }}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              maxWidth: "90%",
              width: "100%",
              maxHeight: "90vh"
            }
          }}
        >
          <DialogTitle sx={{ position: "relative", pb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Performance Graph
              </Typography>
              <IconButton 
                onClick={() => setGraphDialogOpen(false)} 
                size="small"
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: "text.secondary",
                  "&:hover": {
                    bgcolor: "action.hover"
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2} alignItems="center">
                {/* Consultant Selection Dropdown with Search */}
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Select Consultants (Optional)</InputLabel>
                    <Select
                      multiple
                      value={selectedConsultantsForGraph}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSelectedConsultantsForGraph(typeof value === 'string' ? value.split(',') : value);
                      }}
                      label="Select Consultants (Optional)"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: "60px", overflowY: "auto" }}>
                          {selected.length === 0 ? (
                            <Typography variant="body2" color="textSecondary">All Consultants</Typography>
                          ) : (
                            selected
                              .filter(v => v != null)
                              .map((value) => {
                                const consultantName = typeof value === 'string' ? value : value?.name || value || '';
                                if (!consultantName) return null;
                                return (
                                  <Chip 
                                    key={consultantName} 
                                    label={String(consultantName).substring(0, 18)} 
                                    size="small"
                                    sx={{ height: "22px", fontSize: "0.7rem" }}
                                    onDelete={() => {
                                      setSelectedConsultantsForGraph(selected.filter(v => {
                                        const vName = typeof v === 'string' ? v : v?.name || v || '';
                                        return vName !== consultantName;
                                      }));
                                    }}
                                  />
                                );
                              })
                              .filter(item => item != null)
                          )}
                        </Box>
                      )}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 400,
                          },
                        },
                      }}
                    >
                      {/* Search Box */}
                      <Box sx={{ p: 1, borderBottom: "1px solid #e0e0e0" }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Search consultants..."
                          value={consultantSearchText}
                          onChange={(e) => setConsultantSearchText(e.target.value)}
                          InputProps={{
                            startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
                          }}
                          sx={{ mb: 1 }}
                        />
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setSelectedConsultantsForGraph([]);
                              setConsultantSearchText("");
                            }}
                            sx={{ fontSize: "0.7rem", px: 1 }}
                          >
                            Clear
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              const consultantList = allConsultants.length > 0 ? allConsultants : consultants;
                              const allConsultantNames = consultantList
                                .filter(c => c != null) // Filter out null/undefined
                                .map(c => c?.name || c?.consultantName || String(c || ''));
                              setSelectedConsultantsForGraph(allConsultantNames);
                            }}
                            sx={{ fontSize: "0.7rem", px: 1 }}
                          >
                            All
                          </Button>
                        </Box>
                      </Box>
                      
                      {/* Consultant List - Show all consultants */}
                      {(allConsultants.length > 0 ? allConsultants : consultants)
                        .filter(consultant => consultant != null) // Filter out null/undefined first
                        .filter(consultant => {
                          if (!consultantSearchText) return true;
                          const name = consultant?.name || consultant?.consultantName || String(consultant || '');
                          return name.toLowerCase().includes(consultantSearchText.toLowerCase());
                        })
                        .map((consultant) => {
                          if (!consultant) return null; // Safety check
                          const consultantName = consultant?.name || consultant?.consultantName || String(consultant || '');
                          const isSelected = selectedConsultantsForGraph.some(selected => {
                            const selectedName = typeof selected === 'string' ? selected : selected?.name || selected;
                            return String(selectedName) === String(consultantName);
                          });
                          return (
                            <MenuItem 
                              key={consultant?.consultantId || consultantName} 
                              value={consultantName}
                            >
                              <Checkbox checked={isSelected} />
                              <ListItemText primary={consultantName} />
                            </MenuItem>
                          );
                        })
                        .filter(item => item != null) // Remove any null items
                      }
                    </Select>
                  </FormControl>
                </Grid>

                {/* Field Selection Dropdown - Multiple */}
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Select Fields (Multiple)</InputLabel>
                    <Select
                      multiple
                      value={selectedFields}
                      onChange={(e) => {
                        const value = e.target.value;
                        const newFields = typeof value === 'string' ? value.split(',') : value;
                        // Limit to maximum 4 fields
                        if (newFields.length <= 4) {
                          setSelectedFields(newFields);
                        } else {
                          toastMessage("Maximum 4 fields can be selected", "warning");
                        }
                      }}
                      label="Select Fields (Multiple)"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip 
                              key={value} 
                              label={getColumnDisplayName(value)} 
                              size="small"
                              sx={{ height: "24px" }}
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {allColumns
                        .filter(col => ![
                          'name', 'email', 'phone', 'phonePrefix',
                          'gender', 'profileImage', 'status', 'goLive', 'price', 'highPriority',
                          'highestQualification', 'primarySkills', 'otherSkills', 'expertise',
                          'greenTick', 'bio', 'experienceYear', 'dailyWorkHour', 'language',
                          'city', 'state', 'pincode', 'address'
                        ].includes(col))
                        .map((col) => (
                          <MenuItem key={col} value={col}>
                            <Checkbox checked={selectedFields.indexOf(col) > -1} />
                            <ListItemText primary={getColumnDisplayName(col)} />
                          </MenuItem>
                        ))}
                      {Array.isArray(customMetrics) && customMetrics.map((metric) => (
                        <MenuItem key={metric.name} value={metric.name}>
                          <Checkbox checked={selectedFields.indexOf(metric.name) > -1} />
                          <ListItemText primary={metric.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Graph Type */}
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Graph Type</InputLabel>
                    <Select
                      value={graphType}
                      onChange={(e) => setGraphType(e.target.value)}
                      label="Graph Type"
                    >
                      <MenuItem value="line">Line Graph</MenuItem>
                      <MenuItem value="area">Area Graph</MenuItem>
                      <MenuItem value="bar">Bar Graph</MenuItem>
                      <MenuItem value="pie">Pie Chart</MenuItem>
                      <MenuItem value="donut">Donut Chart</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Generate Button */}
                <Grid item xs={12} md={2}>
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handleFetchGraph}
                    disabled={graphLoading || selectedFields.length === 0}
                    startIcon={graphLoading ? <CircularProgress size={20} color="inherit" /> : <BarChartIcon />}
                    sx={{ 
                      py: 1.5,
                      fontSize: "1rem",
                      fontWeight: 600
                    }}
                  >
                    {graphLoading ? "Generating..." : "Generate Graph"}
                  </Button>
                </Grid>
              </Grid>
            </Box>

            {chartData && chartData.series && Array.isArray(chartData.series) && chartData.series.length > 0 ? (
              <>
                {/* Graph Title and Subtitle */}
                {/* <Box sx={{ mb: 3, textAlign: "center" }}>
                  <Typography 
                    variant="h4"
                    sx={{ 
                      mb: 1, 
                      fontWeight: 700, 
                      color: "#212121",
                      fontSize: { xs: "1.5rem", md: "2rem" }
                    }}
                  >
                    {selectedFields.length > 0 
                      ? `${selectedFields.map(f => getColumnDisplayName(f)).join(" & ")} Analytics`
                      : "Performance Analytics"}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: "#757575",
                      fontSize: "1rem",
                      fontWeight: 500
                    }}
                  >
                    Track your performance metrics over time
                  </Typography>
                </Box> */}
                
                {/* Date Range Info */}
                <Box sx={{ mb: 3, p: 2.5, bgcolor: "#e3f2fd", borderRadius: 2, border: "2px solid #90caf9" }}>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: "#1976d2", mb: 1, fontSize: "1rem" }}>
                      📅 Data Range:
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#424242", fontSize: "0.95rem", fontWeight: 500 }}>
                      {dateFilterType === "all_time" ? (
                        <>
                          <strong>From:</strong> January 2025 
                          {" "} <strong>To:</strong> {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                          <Box component="span" sx={{ ml: 1, fontSize: "0.85rem", color: "#757575" }}>
                            (Month-wise)
                          </Box>
                        </>
                      ) : (
                        <>
                          <strong>From:</strong> {formatDateByRange(startDate, getDateRangeFormat(startDate, endDate))} 
                          {" "} <strong>To:</strong> {formatDateByRange(endDate, getDateRangeFormat(startDate, endDate))}
                          <Box component="span" sx={{ ml: 1, fontSize: "0.85rem", color: "#757575" }}>
                            ({getDateRangeFormat(startDate, endDate) === 'hour' ? 'Hour-wise' : 
                              getDateRangeFormat(startDate, endDate) === 'day' ? 'Day-wise' :
                              getDateRangeFormat(startDate, endDate) === 'month' ? 'Month-wise' : 'Year-wise'})
                          </Box>
                        </>
                      )}
                      {selectedFields.length > 0 && (
                        <>
                          <br />
                          <Box component="span" sx={{ mt: 1, display: "inline-block" }}>
                            Metrics: <strong>{selectedFields.map(f => getColumnDisplayName(f)).join(", ")}</strong>
                          </Box>
                        </>
                      )}
                      {selectedConsultantsForGraph.length > 0 ? (
                        <>
                          <br />
                          <Box component="span" sx={{ mt: 1, display: "inline-block" }}>
                            <strong>Selected Consultants ({selectedConsultantsForGraph.length}):</strong>
                            <Box component="span" sx={{ ml: 1, fontSize: "0.9rem" }}>
                              {selectedConsultantsForGraph
                                .filter(c => c != null)
                                .map((c, idx) => {
                                  const consultantName = typeof c === 'string' ? c : c?.name || c || '';
                                  return consultantName;
                                })
                                .filter(name => name)
                                .slice(0, 5)
                                .map((name, idx) => (
                                  <Chip
                                    key={idx}
                                    label={name}
                                    size="small"
                                    sx={{ 
                                      height: "22px", 
                                      fontSize: "0.75rem",
                                      mr: 0.5,
                                      mb: 0.5,
                                      bgcolor: "#e3f2fd",
                                      color: "#1976d2",
                                      fontWeight: 600
                                    }}
                                  />
                                ))}
                              {selectedConsultantsForGraph.filter(c => c != null).length > 5 && (
                                <Chip
                                  label={`+${selectedConsultantsForGraph.filter(c => c != null).length - 5} more`}
                                  size="small"
                                  sx={{ 
                                    height: "22px", 
                                    fontSize: "0.75rem",
                                    mr: 0.5,
                                    mb: 0.5,
                                    bgcolor: "#f5f5f5",
                                    color: "#757575"
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        </>
                      ) : (
                        <>
                          <br />
                          <Box component="span" sx={{ mt: 1, display: "inline-block", fontSize: "0.9rem", color: "#757575" }}>
                            Showing data for: <strong>All Consultants</strong>
                          </Box>
                        </>
                      )}
                    </Typography>
                </Box>
                
                <Box sx={{ 
                  bgcolor: "white", 
                  borderRadius: 3, 
                  p: { xs: 2, sm: 4 }, 
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                  overflow: "hidden"
                }}>
                  {graphLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '350px' }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <Box sx={{ 
                      overflowX: "auto",
                      overflowY: "hidden",
                      width: "100%",
                      "&::-webkit-scrollbar": {
                        height: "8px",
                      },
                      "&::-webkit-scrollbar-track": {
                        background: "#f1f1f1",
                        borderRadius: "4px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        background: "#888",
                        borderRadius: "4px",
                        "&:hover": {
                          background: "#555",
                        },
                      },
                    }}>
                      <Box sx={{ minWidth: { xs: "600px", sm: "100%" } }}>
                        <ReactApexChart
                          options={chartData?.options}
                          series={chartData?.series}
                          type={graphType === "bar" ? "bar" : graphType === "line" ? "line" : "area"}
                          height={350}
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              </>
            ) : (
              <Box sx={{ 
                bgcolor: "white", 
                borderRadius: 3, 
                p: 5, 
                textAlign: "center",
                border: "2px dashed #e0e0e0",
                minHeight: "400px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center"
              }}>
                <BarChartIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
                <Typography variant="h6" color="textSecondary" sx={{ mb: 1 }}>
                  No Graph Data
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Select fields and click "Generate Graph" to visualize data
                </Typography>
              </Box>
            )}
          </DialogContent>
        </Dialog>
        </Box>
      </div>
    </div>
  );
};

export default ConsultantPerformance;

